import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CalendarClock,
  Check,
  CheckCircle2,
  Loader2,
  LogIn,
  LogOut,
  Plus,
  ShieldCheck,
  Trash2,
  UserRoundPen,
  XCircle,
} from "lucide-react";

import { fireTaskCompletedConfetti } from "../utils/confetti";
import { SwalWithTheme } from "../utils/sweetalert";
import { useAppAlert } from "../context/alert-context";
import FiltersSheet from "../components/FiltersSheet";
import usePageFilters from "../hooks/usePageFilters";
import { getUsuarioLogado } from "../services/LoginService";
import { listarUsuariosPaginado } from "../services/usuarioService";
import { formatMoneyBRL, parseMoneyBRL } from "../utils/money";
import {
  listarTarefasPaginado,
  buscarTarefa,
  criarTarefa,
  atualizarTarefa,
  removerTarefa,
  checkinTarefa,
  checkoutTarefa,
  fallbackCheckinTarefa,
  fallbackCheckoutTarefa,
  evidenciarTarefa,
  concluirTarefaComPayload,
  listarChecksTarefa,
  listarHistoricoTarefa,
  listarPendenciasAprovacao,
  aprovarCheckTarefa,
  reprovarCheckTarefa,
} from "../services/tarefaService";
import httpClient from "../services/httpClient";
import {
  APP_NOTIFICATION_CREATED_EVENT,
  getNotificationTaskId,
  isApprovalNotification,
  isApprovalPendingNotification,
} from "../services/notificationService";
import { listarParceiros } from "../services/parceiroService";
import { listarTiposDeTarefa } from "../services/tipoDeTarefaService";
import { listarStatusAtividades } from "../services/statusAtividadeService";
import { listarMotivosTarefa } from "../services/motivoTarefaService";
import { listarBancosAtivos, listarConveniosAtivos } from "../services/joinService";
import { listarConcorrentes } from "../services/concorrenteService";
import { getCurrentMonthRange } from "../utils/dateFilters";
import { buildTaskGeoPartner, parseGeoErrorInfo } from "../utils/taskGeoCheck";
import { buildTaskTitleFromNotes } from "../utils/taskText";
import { resolveTaskTypeIconDefinition } from "../utils/taskTypeIcons";
import {
  TASK_FIELD_CODES,
  allowsMultiplePartners,
  allowsNoPartner,
  getStepPolicy,
  hasVisibleField,
  isFieldRequired,
  isFieldVisible,
  isStepEnabled,
  mergeFieldModes,
  requiresCheckin,
  requiresCheckout,
  stepRequiresApproval,
  stepRequiresImage,
} from "../utils/taskRules";
import { toneClassMap } from "../utils/themeContrast";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Checkbox,
  DataTable,
  DatePickerField,
  DateRangeField,
  FormField,
  Modal,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  MultiSelectField,
  Skeleton,
  SearchBar,
  StatusBadge,
  getAvatarInitials,
} from "../components/ui";
import TaskGeoCheckDrawer from "../components/tasks/TaskGeoCheckDrawer";

const PAGE_SIZE = 20;
const TASK_SCHEDULE_WINDOW_DAYS = 90;
const TASK_SCHEDULE_PAST_WINDOW_DAYS = 14;
const FILTER_STORAGE_KEY = "lista-tarefas:filtros:v3";
const MOTIVOS_VISIBLE_BUDGET = 2;
const CONCLUIR_DRAFT_STORAGE_PREFIX = "lista-tarefas:concluir-draft:";

const calcProgress = (checks = []) => {
  const total = checks.length || 1;
  const done = checks.filter(Boolean).length;
  return Math.round((done / total) * 100);
};

function getConcluirDraftStorageKey(taskId) {
  return `${CONCLUIR_DRAFT_STORAGE_PREFIX}${taskId}`;
}

function loadConcluirDraft(taskId) {
  if (!taskId || typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(getConcluirDraftStorageKey(taskId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      relatorio: String(parsed?.relatorio || ""),
      produto: String(parsed?.produto || ""),
      comissaoParceiroAjustada:
        typeof parsed?.comissaoParceiroAjustada === "boolean" ? parsed.comissaoParceiroAjustada : null,
      loginParceiroSolicitado:
        typeof parsed?.loginParceiroSolicitado === "boolean" ? parsed.loginParceiroSolicitado : null,
      bancosConvenios: Array.isArray(parsed?.bancosConvenios)
        ? parsed.bancosConvenios.map((item) => ({
          ...createBancoLinha(),
          ...item,
          bancoId: String(item?.bancoId || ""),
          convenioId: String(item?.convenioId || ""),
          valorProduzido: String(item?.valorProduzido || ""),
          concorrenteId: String(item?.concorrenteId || ""),
          concorrenteOutro: String(item?.concorrenteOutro || ""),
        }))
        : null,
      fallbackMotivo: String(parsed?.fallbackMotivo || ""),
      evidenciaObservacao: String(parsed?.evidenciaObservacao || ""),
    };
  } catch {
    return null;
  }
}

function clearConcluirDraft(taskId) {
  if (!taskId || typeof window === "undefined") return;
  window.sessionStorage.removeItem(getConcluirDraftStorageKey(taskId));
}

function loadTaskFiltersFromSession() {
  const defaultRange = getCurrentMonthRange();
  const toIdArray = (value) =>
    (Array.isArray(value) ? value : [])
      .map((item) => String(item ?? "").trim())
      .filter((item) => /^\d+$/.test(item));

  const fallback = {
    page: 0,
    pageSize: PAGE_SIZE,
    searchTerm: "",
    filterStatusIds: [],
    filterParceiroIds: [],
    filterResponsavelIds: [],
    filterTipoIds: [],
    filtroDataInicio: defaultRange.from,
    filtroDataFim: defaultRange.to,
  };

  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return {
      ...fallback,
      page: Number.isFinite(parsed?.page) ? Math.max(0, Number(parsed.page)) : fallback.page,
      pageSize: Number.isFinite(parsed?.pageSize) ? Number(parsed.pageSize) : fallback.pageSize,
      searchTerm: String(parsed?.searchTerm || ""),
      filterStatusIds: toIdArray(parsed?.filterStatusIds),
      filterParceiroIds: toIdArray(parsed?.filterParceiroIds),
      filterResponsavelIds: toIdArray(parsed?.filterResponsavelIds),
      filterTipoIds: toIdArray(parsed?.filterTipoIds),
      filtroDataInicio: parsed?.filtroDataInicio || fallback.filtroDataInicio,
      filtroDataFim: parsed?.filtroDataFim || fallback.filtroDataFim,
    };
  } catch {
    return fallback;
  }
}

function toDateTimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function normalizeDateTime(value) {
  if (!value) return null;
  const normalized = value.length === 16 ? `${value}:00` : value;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(normalized);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    const localDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second || 0),
      0
    );
    if (!Number.isNaN(localDate.getTime())) {
      return localDate.toISOString();
    }
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function buildTaskScheduleBounds(now = new Date()) {
  const minDate = new Date(now);
  minDate.setDate(minDate.getDate() - TASK_SCHEDULE_PAST_WINDOW_DAYS);
  minDate.setHours(0, 0, 0, 0);
  const min = toDateTimeLocal(minDate);
  const maxDate = new Date(now);
  maxDate.setDate(maxDate.getDate() + TASK_SCHEDULE_WINDOW_DAYS);
  return {
    min,
    max: toDateTimeLocal(maxDate),
  };
}

function isTaskScheduleOutsideWindow(value, bounds) {
  if (!value || !bounds?.min || !bounds?.max) return false;
  const selectedDate = new Date(normalizeDateTime(value));
  const minDate = new Date(normalizeDateTime(bounds.min));
  const maxDate = new Date(normalizeDateTime(bounds.max));

  if ([selectedDate, minDate, maxDate].some((date) => Number.isNaN(date.getTime()))) {
    return false;
  }

  return selectedDate < minDate || selectedDate > maxDate;
}

function parseJsonSafe(value, fallback) {
  if (!value || typeof value !== "string") return fallback;
  try {
    const parsed = JSON.parse(value);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function parseMotivos(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  return String(value)
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function createBancoLinha() {
  return {
    bancoId: "",
    convenioId: "",
    valorProduzido: "",
    concorrenteId: "",
    concorrenteOutro: "",
  };
}

function mapDadosFinanceirosParaLinhas(dadosFinanceiros = []) {
  if (!Array.isArray(dadosFinanceiros) || dadosFinanceiros.length === 0) {
    return [createBancoLinha()];
  }

  const linhas = [];
  dadosFinanceiros.forEach((item) => {
    const bancoId = String(item?.bancoId || item?.banco?.id || "");
    const concorrenteId = String(item?.concorrenteId || item?.concorrente?.id || "");
    const concorrenteOutro = String(item?.concorrenteOutro || "");

    if (item?.convenioId || item?.convenioNome || item?.valorProduzido != null) {
      linhas.push({
        ...createBancoLinha(),
        bancoId,
        convenioId: String(item.convenioId || ""),
        valorProduzido: item.valorProduzido != null ? String(item.valorProduzido) : "",
        concorrenteId,
        concorrenteOutro,
      });
      return;
    }

    const conveniosArray = Array.isArray(item?.convenios) ? item.convenios : [];
    if (!conveniosArray.length) {
      linhas.push({
        ...createBancoLinha(),
        bancoId,
        valorProduzido: item?.valorProduzido != null ? String(item.valorProduzido) : "",
        concorrenteId,
        concorrenteOutro,
      });
      return;
    }

    conveniosArray.forEach((convenio) => {
      linhas.push({
        ...createBancoLinha(),
        bancoId,
        convenioId: String(convenio?.convenioId || convenio?.ConvenioId || convenio?.id || ""),
        valorProduzido: item?.valorProduzido != null ? String(item.valorProduzido) : "",
        concorrenteId,
        concorrenteOutro,
      });
    });
  });

  return linhas.length ? linhas : [createBancoLinha()];
}

function normalizeKey(text) {
  return String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function resolveCheckFileUrl(path) {
  const raw = String(path || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = String(httpClient?.defaults?.baseURL || "").replace(/\/$/, "");
  const suffix = raw.startsWith("/") ? raw : `/${raw}`;
  return `${base}${suffix}`;
}

export default function ListaTarefas() {
  const navigate = useNavigate();
  const location = useLocation();
  const alert = useAppAlert();
  const defaultDateRange = useMemo(() => getCurrentMonthRange(), []);
  const initialFilters = useMemo(() => loadTaskFiltersFromSession(), []);
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [taskPage, setTaskPage] = useState({
    content: [],
    page: 0,
    totalPages: 0,
    totalElements: 0,
    size: PAGE_SIZE,
  });
  const [page, setPage] = useState(initialFilters.page);
  const [pageSize, setPageSize] = useState(initialFilters.pageSize || PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialFilters.searchTerm);

  const [tiposTarefa, setTiposTarefa] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [parceiros, setParceiros] = useState([]);
  const [usuariosResponsaveis, setUsuariosResponsaveis] = useState([]);
  const [motivosList, setMotivosList] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [concorrentes, setConcorrentes] = useState([]);
  const [metaLoading, setMetaLoading] = useState(true);

  const [filterStatusIds, setFilterStatusIds] = useState(initialFilters.filterStatusIds);
  const [filterParceiroIds, setFilterParceiroIds] = useState(initialFilters.filterParceiroIds);
  const [filterResponsavelIds, setFilterResponsavelIds] = useState(initialFilters.filterResponsavelIds);
  const [filterTipoIds, setFilterTipoIds] = useState(initialFilters.filterTipoIds);
  const [filtroDataInicio, setFiltroDataInicio] = useState(initialFilters.filtroDataInicio);
  const [filtroDataFim, setFiltroDataFim] = useState(initialFilters.filtroDataFim);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [motivosExpandidos, setMotivosExpandidos] = useState(new Set());
  // Tarefa com 61 parceiros estourava a linha da tabela ao expandir. A lista inteira passou a abrir
  // num painel lateral, com busca.
  const [painelParceiros, setPainelParceiros] = useState({ aberto: false, titulo: "", itens: [] });
  const [buscaPainelParceiros, setBuscaPainelParceiros] = useState("");
  const notificationOpenRef = useRef(null);
  const approvalNotificationRefreshRef = useRef({
    isAdmin: false,
    execTaskId: null,
    refreshTasks: async () => { },
    refreshPendingApprovals: async () => { },
    refreshChecks: async () => { },
  });
  const approvalNotificationTimeoutRef = useRef(null);
  const fallbackSubmitInFlightRef = useRef(false);

  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState({
    tipoIds: [],
    parceiroIds: [],
    usuarioIds: [],
    vincularParceiros: true,
    motivoIds: [],
    dataHora: "",
    observacoes: "",
  });
  const [savingTaskModal, setSavingTaskModal] = useState(false);
  // Uma tarefa por parceiro e o padrao: com todos juntos, a tarefa so pode ser concluida de uma vez
  // e ninguem consegue dizer "visitei 40, faltam 21".
  const [umaTarefaPorParceiro, setUmaTarefaPorParceiro] = useState(true);
  // Atalho para quem registra o que ja fez: cria e conclui na mesma acao.
  const [registrarComoConcluida, setRegistrarComoConcluida] = useState(false);

  const [showExecModal, setShowExecModal] = useState(false);
  const [execTask, setExecTask] = useState(null);
  const [checks, setChecks] = useState([]);
  const [execTaskHistory, setExecTaskHistory] = useState([]);
  const [execTaskHistoryLoading, setExecTaskHistoryLoading] = useState(false);
  const [execLoading, setExecLoading] = useState(false);
  const [concluirForm, setConcluirForm] = useState({
    relatorio: "",
    produto: "",
    comissaoParceiroAjustada: null,
    loginParceiroSolicitado: null,
    bancosConvenios: [createBancoLinha()],
  });
  const [fallbackForm, setFallbackForm] = useState({
    motivo: "",
    files: [],
  });
  const [evidenciaForm, setEvidenciaForm] = useState({
    observacao: "",
    files: [],
  });
  const [fallbackLiberado, setFallbackLiberado] = useState({
    habilitado: false,
    acao: null,
    mensagem: "",
  });
  const [geoCheckDrawer, setGeoCheckDrawer] = useState({
    open: false,
    action: "checkin",
  });
  const [acaoLoading, setAcaoLoading] = useState(false);
  const [pendenciasAprovacao, setPendenciasAprovacao] = useState([]);
  const [pendenciasLoading, setPendenciasLoading] = useState(false);
  const [aprovarLoadingId, setAprovarLoadingId] = useState(null);

  const taskScheduleBounds = useMemo(() => buildTaskScheduleBounds(), [showModal, editingTask?.id]);

  const fallbackPreviews = useMemo(
    () =>
      (fallbackForm.files || []).map((file) => ({
        name: file.name,
        url: URL.createObjectURL(file),
      })),
    [fallbackForm.files]
  );

  const evidenciaPreviews = useMemo(
    () =>
      (evidenciaForm.files || []).map((file) => ({
        name: file.name,
        url: URL.createObjectURL(file),
      })),
    [evidenciaForm.files]
  );

  useEffect(
    () => () => {
      fallbackPreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    },
    [fallbackPreviews]
  );

  useEffect(
    () => () => {
      evidenciaPreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    },
    [evidenciaPreviews]
  );

  useEffect(() => {
    if (!showExecModal || !execTask?.id || typeof window === "undefined") return;
    const payload = {
      relatorio: concluirForm.relatorio || "",
      produto: concluirForm.produto || "",
      comissaoParceiroAjustada: concluirForm.comissaoParceiroAjustada,
      loginParceiroSolicitado: concluirForm.loginParceiroSolicitado,
      bancosConvenios: concluirForm.bancosConvenios || [createBancoLinha()],
      fallbackMotivo: fallbackForm.motivo || "",
      evidenciaObservacao: evidenciaForm.observacao || "",
      updatedAt: Date.now(),
    };
    window.sessionStorage.setItem(getConcluirDraftStorageKey(execTask.id), JSON.stringify(payload));
  }, [
    showExecModal,
    execTask?.id,
    concluirForm,
    fallbackForm.motivo,
    evidenciaForm.observacao,
  ]);

  useEffect(() => {
    (async () => {
      try {
        setMetaLoading(true);
        const user = await getUsuarioLogado();
        setUsuarioLogado(user);

        const [
          tiposResult,
          statusResult,
          parceirosResult,
          usuariosResult,
          motivosResult,
          bancosResult,
          conveniosResult,
          concorrentesResult,
        ] = await Promise.allSettled([
          listarTiposDeTarefa(),
          listarStatusAtividades(),
          listarParceiros(),
          user?.cargo?.toLowerCase() === "admin"
            ? listarUsuariosPaginado({ page: 0, size: 200, ativo: true })
            : Promise.resolve({ content: [] }),
          listarMotivosTarefa(),
          listarBancosAtivos(),
          listarConveniosAtivos(),
          listarConcorrentes(),
        ]);

        setTiposTarefa(tiposResult.status === "fulfilled" && Array.isArray(tiposResult.value) ? tiposResult.value : []);
        setStatusList(statusResult.status === "fulfilled" && Array.isArray(statusResult.value) ? statusResult.value : []);
        setParceiros(parceirosResult.status === "fulfilled" && Array.isArray(parceirosResult.value) ? parceirosResult.value : []);
        setUsuariosResponsaveis(
          usuariosResult.status === "fulfilled" && Array.isArray(usuariosResult.value?.content)
            ? usuariosResult.value.content.filter((entry) => String(entry?.cargo || "").toLowerCase() === "user")
            : []
        );
        setMotivosList(motivosResult.status === "fulfilled" && Array.isArray(motivosResult.value) ? motivosResult.value : []);
        setBancos(bancosResult.status === "fulfilled" && Array.isArray(bancosResult.value) ? bancosResult.value : []);
        setConvenios(conveniosResult.status === "fulfilled" && Array.isArray(conveniosResult.value) ? conveniosResult.value : []);
        setConcorrentes(concorrentesResult.status === "fulfilled" && Array.isArray(concorrentesResult.value) ? concorrentesResult.value : []);
      } catch {
        SwalWithTheme.error("Erro", "Falha ao carregar dados de tarefas");
      } finally {
        setMetaLoading(false);
      }
    })();
  }, []);

  const isAdmin = useMemo(
    () => String(usuarioLogado?.cargo || "").trim().toLowerCase() === "admin",
    [usuarioLogado]
  );

  const carregarPendenciasAprovacao = async () => {
    if (!isAdmin) {
      setPendenciasAprovacao([]);
      return;
    }
    setPendenciasLoading(true);
    try {
      const data = await listarPendenciasAprovacao();
      setPendenciasAprovacao(Array.isArray(data) ? data : []);
    } catch {
      setPendenciasAprovacao([]);
    } finally {
      setPendenciasLoading(false);
    }
  };

  useEffect(() => {
    carregarPendenciasAprovacao();
  }, [isAdmin]);


  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({
        page,
        pageSize,
        searchTerm,
        filterStatusIds,
        filterParceiroIds,
        filterResponsavelIds,
        filterTipoIds,
        filtroDataInicio,
        filtroDataFim,
      })
    );
  }, [
    page,
    pageSize,
    searchTerm,
    filterStatusIds,
    filterParceiroIds,
    filterResponsavelIds,
    filterTipoIds,
    filtroDataInicio,
    filtroDataFim,
  ]);

  useEffect(() => {
    carregarTarefas();
  }, [page, pageSize, searchTerm, filterStatusIds, filterParceiroIds, filterResponsavelIds, filterTipoIds, filtroDataInicio, filtroDataFim, statusList]);

  const carregarTarefas = async ({ silent = false } = {}) => {
    const statusIdsValidos = filterStatusIds.filter((id) => /^\d+$/.test(String(id)));
    const parceiroIdsValidos = filterParceiroIds.filter((id) => /^\d+$/.test(String(id)));
    const responsavelIdsValidos = filterResponsavelIds.filter((id) => /^\d+$/.test(String(id)));
    const tipoIdsValidos = filterTipoIds.filter((id) => /^\d+$/.test(String(id)));

    const incluirInativas = filterStatusIds.some((statusId) => {
      const status = statusList.find((item) => String(item.id) === String(statusId));
      const nome = String(status?.nome || "").trim().toLowerCase();
      return nome.includes("cancel");
    });
    setLoading(true);
    try {
      const data = await listarTarefasPaginado({
        page,
        size: pageSize,
        q: searchTerm.trim(),
        statusIds: statusIdsValidos.map(Number),
        parceiroIds: parceiroIdsValidos.map(Number),
        usuarioIds: responsavelIdsValidos.map(Number),
        tipoIds: tipoIdsValidos.map(Number),
        dataInicio: filtroDataInicio || undefined,
        dataFim: filtroDataFim || undefined,
        incluirInativas,
      });
      setTaskPage(data);
    } catch (error) {
      if (!silent) {
        const message = error?.response?.data?.message || error?.response?.data?.error || "Erro ao carregar tarefas";
        SwalWithTheme.error("Erro", message);
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedTipo = useMemo(() => {
    const id = form.tipoIds?.[0];
    return tiposTarefa.find((t) => String(t.id) === String(id)) || null;
  }, [form.tipoIds, tiposTarefa]);

  const permiteMultiplosParceirosNoTipo = allowsMultiplePartners(selectedTipo);
  // "Ja fiz" so vale onde o backend aceitaria a conclusao direta: sem check-in, sem check-out e sem
  // evidencia obrigatoria. Nos outros tipos a prova faz parte da tarefa.
  const podeRegistrarConcluida = useMemo(() => {
    if (!selectedTipo || editingTask) return false;
    if (requiresCheckin(selectedTipo) || requiresCheckout(selectedTipo)) return false;
    const execucao = getStepPolicy(selectedTipo, "execucao");
    return !(isStepEnabled(execucao) && stepRequiresImage(execucao));
  }, [selectedTipo, editingTask]);
  const permiteSemParceiroNoTipo = allowsNoPartner(selectedTipo);
  const vinculoSomenteUsuario = permiteSemParceiroNoTipo && !form.vincularParceiros;

  const resolveSelectedPartner = (partnerId) => parceirosMap.get(String(partnerId)) || null;

  /** Uma tarefa por parceiro: cada uma se conclui sozinha e o andamento fica visivel. */
  const buildTaskGroupsPorParceiro = (partnerIds = []) =>
    partnerIds.map((parceiroId) => {
      const partner = resolveSelectedPartner(parceiroId);
      const responsavelId = partner?.usuarioId ?? partner?.usuario?.id ?? null;
      return {
        groupKey: `P:${parceiroId}`,
        responsavelId: responsavelId != null ? Number(responsavelId) : null,
        partnerIds: [Number(parceiroId)],
      };
    });

  const buildTaskGroupsByResponsavel = (partnerIds = []) => {
    const groups = new Map();

    partnerIds.forEach((parceiroId) => {
      const partner = resolveSelectedPartner(parceiroId);
      const responsavelId = partner?.usuarioId ?? partner?.usuario?.id ?? null;
      const groupKey = responsavelId != null ? String(responsavelId) : "SEM_RESPONSAVEL";

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          groupKey,
          responsavelId: responsavelId != null ? Number(responsavelId) : null,
          partnerIds: [],
        });
      }

      groups.get(groupKey).partnerIds.push(Number(parceiroId));
    });

    return Array.from(groups.values());
  };

  const tarefasFiltradas = taskPage.content || [];

  const resetForm = () => {
    const currentIsAdmin = String(usuarioLogado?.cargo || "").trim().toLowerCase() === "admin";
    setEditingTask(null);
    setForm({
      tipoIds: [],
      parceiroIds: [],
      usuarioIds: currentIsAdmin ? [] : (usuarioLogado?.id ? [String(usuarioLogado.id)] : []),
      vincularParceiros: true,
      motivoIds: [],
      dataHora: "",
      observacoes: "",
    });
    setRegistrarComoConcluida(false);
    setUmaTarefaPorParceiro(true);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const getStatusNome = (task) => {
    const status = statusList.find((item) => String(item.id) === String(task?.idStatusAtividade || ""));
    return String(status?.nome || "").trim().toLowerCase();
  };

  const isTaskConcluida = (task) => {
    if (!task) return false;
    if (task.dataConclusao) return true;
    return getStatusNome(task).includes("conclu");
  };

  const isTaskCancelada = (task) => {
    if (!task) return false;
    return getStatusNome(task).includes("cancel");
  };

  const podeEditarDataHora = (task) => {
    if (!usuarioLogado) return false;
    if (isTaskConcluida(task) || isTaskCancelada(task)) return false;
    const isAdmin = String(usuarioLogado.cargo || "").toLowerCase() === "admin";
    if (isAdmin) return true;
    return String(task.idUsuarioCriador || "") === String(usuarioLogado.id || "");
  };

  const podeEditar = (task) => {
    if (!usuarioLogado || !task) return false;
    if (isTaskConcluida(task) || isTaskCancelada(task)) return false;
    const isAdminCargo = String(usuarioLogado.cargo || "").toLowerCase() === "admin";
    if (isAdminCargo) return true;
    return String(task.idUsuario || "") === String(usuarioLogado.id || "");
  };

  const podeCancelar = (task) => {
    if (!usuarioLogado || !task) return false;
    if (isTaskConcluida(task) || isTaskCancelada(task)) return false;
    const isAdminCargo = String(usuarioLogado.cargo || "").toLowerCase() === "admin";
    if (isAdminCargo) return true;
    return (
      String(task.idUsuario || "") === String(usuarioLogado.id || "") &&
      String(task.idUsuarioCriador || "") === String(usuarioLogado.id || "")
    );
  };

  const podeConcluir = (task) => {
    if (!usuarioLogado || !task) return false;
    if (isTaskConcluida(task) || isTaskCancelada(task)) return false;
    const isAdminCargo = String(usuarioLogado.cargo || "").toLowerCase() === "admin";
    if (isAdminCargo) return true;
    return String(task.idUsuario || "") === String(usuarioLogado.id || "");
  };

  const handleEditar = (task) => {
    if (!podeEditar(task)) {
      SwalWithTheme.error("Acao nao permitida", "Tarefas concluidas/canceladas nao podem ser editadas por comercial.");
      return;
    }

    const tipoId = task.idTipoDeTarefa ? String(task.idTipoDeTarefa) : "";
    const tipo = tiposTarefa.find((t) => String(t.id) === tipoId);
    const parceirosPre = (task.parceirosIds || []).map((pid) => String(pid));
    const parceiroIds = allowsMultiplePartners(tipo) ? parceirosPre : parceirosPre.slice(0, 1);

    setEditingTask(task);
    setForm({
      tipoIds: tipoId ? [tipoId] : [],
      parceiroIds,
      usuarioIds: task.idUsuario ? [String(task.idUsuario)] : [],
      vincularParceiros: parceiroIds.length > 0 || !allowsNoPartner(tipo),
      motivoIds: (task.motivosIds || []).map((id) => String(id)),
      dataHora: toDateTimeLocal(task.dataInicio),
      observacoes: task.observacoes || task.descricaoTarefa || "",
    });
    setShowModal(true);
  };

  const handleSalvarTarefa = async () => {
    if (savingTaskModal) return;

    if (editingTask?.id && !podeEditar(editingTask)) {
      SwalWithTheme.error("Acao nao permitida", "Essa tarefa nao pode ser editada no seu perfil.");
      return;
    }

    const tipoId = form.tipoIds?.[0];
    const parceiroIds = form.vincularParceiros ? (form.parceiroIds || []) : [];
    const motivoIds = form.motivoIds || [];

    if (!tipoId || !form.dataHora || motivoIds.length === 0) {
      const tipo = tiposTarefa.find((t) => String(t.id) === String(tipoId));
      const permiteSemParceiro = allowsNoPartner(tipo);
      const exigeUsuario = permiteSemParceiro && !form.vincularParceiros && isAdmin;
      if (
        !tipoId ||
        !form.dataHora ||
        motivoIds.length === 0 ||
        (!permiteSemParceiro && parceiroIds.length === 0) ||
        (permiteSemParceiro && form.vincularParceiros && parceiroIds.length === 0) ||
        (exigeUsuario && !form.usuarioIds.length)
      ) {
        SwalWithTheme.error("Erro", permiteSemParceiro
          ? "Preencha tipo, data/hora, responsavel e ao menos um motivo"
          : "Preencha tipo, data/hora, parceiro e ao menos um motivo");
        return;
      }
    }

    const tipo = tiposTarefa.find((t) => String(t.id) === String(tipoId));
    if (!tipo) {
      SwalWithTheme.error("Erro", "Tipo de tarefa invalido");
      return;
    }
    if (!allowsNoPartner(tipo) && parceiroIds.length === 0) {
      SwalWithTheme.error("Erro", "Este tipo de tarefa exige ao menos um parceiro");
      return;
    }
    if (allowsNoPartner(tipo) && form.vincularParceiros && parceiroIds.length === 0) {
      SwalWithTheme.error("Erro", "Selecione ao menos um parceiro ou desmarque o vinculo com parceiros");
      return;
    }
    if (!allowsMultiplePartners(tipo) && parceiroIds.length > 1) {
      SwalWithTheme.error("Erro", "Este tipo de tarefa permite apenas um parceiro");
      return;
    }
    if (allowsNoPartner(tipo) && !form.vincularParceiros && isAdmin && !form.usuarioIds.length) {
      SwalWithTheme.error("Erro", "Selecione ao menos um responsavel da tarefa");
      return;
    }

    if (isTaskScheduleOutsideWindow(form.dataHora, taskScheduleBounds)) {
      SwalWithTheme.error(
        "Erro",
        `A data/hora da tarefa deve estar entre ${TASK_SCHEDULE_PAST_WINDOW_DAYS} dias no passado e ${TASK_SCHEDULE_WINDOW_DAYS} dias no futuro`
      );
      return;
    }

    if (
      editingTask &&
      !podeEditarDataHora(editingTask) &&
      normalizeDateTime(form.dataHora) !== normalizeDateTime(toDateTimeLocal(editingTask.dataInicio))
    ) {
      SwalWithTheme.error("Erro", "Data/hora nao pode ser alterada para tarefa atribuida por administrador");
      return;
    }

    try {
      setSavingTaskModal(true);
      const payload = {
        descricaoTarefa: buildTaskTitleFromNotes(form.observacoes),
        dataInicio: normalizeDateTime(form.dataHora),
        observacoes: form.observacoes,
        idTipoDeTarefa: Number(tipoId),
        parceirosIds: parceiroIds.map((pid) => Number(pid)),
        idUsuario: form.usuarioIds.length ? Number(form.usuarioIds[0]) : null,
        motivosIds: motivoIds.map((mid) => Number(mid)),
      };

      if (editingTask?.id) {
        await atualizarTarefa(editingTask.id, payload);
        SwalWithTheme.success("Sucesso", "Tarefa atualizada");
      } else {
        const taskGroups = parceiroIds.length > 0
          ? (umaTarefaPorParceiro ? buildTaskGroupsPorParceiro(parceiroIds) : buildTaskGroupsByResponsavel(parceiroIds))
          : (form.usuarioIds.length
              ? form.usuarioIds.map((id) => ({ groupKey: `U:${id}`, responsavelId: Number(id), partnerIds: [] }))
              : [{ groupKey: "USER_ONLY", responsavelId: null, partnerIds: [] }]);

        const results = await Promise.allSettled(
          taskGroups.map((group) =>
            criarTarefa({
              ...payload,
              parceirosIds: group.partnerIds,
              idUsuario: group.responsavelId ?? payload.idUsuario,
            })
          )
        );
        if (registrarComoConcluida && podeRegistrarConcluida) {
          // Tipo sem check nem evidencia: criar e concluir em seguida poupa a segunda viagem na
          // tela. O backend recusa se o tipo exigir check-in, check-out ou foto -- e deve recusar.
          await Promise.allSettled(
            results
              .filter((resultado) => resultado.status === "fulfilled" && resultado.value?.id)
              .map((resultado) => concluirTarefaComPayload(resultado.value.id, {}))
          );
        }

        const successCount = results.filter((result) => result.status === "fulfilled").length;
        const failureCount = results.length - successCount;

        if (failureCount > 0) {
          const firstError = results.find((result) => result.status === "rejected");
          const message = firstError?.reason?.response?.data?.message || "Erro ao criar tarefa";
          if (successCount > 0) {
            SwalWithTheme.warning("Criacao parcial", `${successCount} tarefa(s) criada(s). ${failureCount} falhou(ram). ${message}`);
          } else {
            SwalWithTheme.error("Erro", message);
            return;
          }
        } else {
          SwalWithTheme.success(
            "Sucesso",
            taskGroups.length === 1
              ? (registrarComoConcluida && podeRegistrarConcluida ? "Tarefa registrada e concluída" : "Tarefa criada")
              : `${taskGroups.length} tarefas ${registrarComoConcluida && podeRegistrarConcluida ? "registradas e concluídas" : "criadas"}`
          );
        }
      }

      setShowModal(false);
      resetForm();
      await carregarTarefas();
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao salvar tarefa";
      SwalWithTheme.error("Erro", message);
    } finally {
      setSavingTaskModal(false);
    }
  };

  const handleRemover = async (task) => {
    if (!podeCancelar(task)) {
      SwalWithTheme.error("Acao nao permitida", "Essa tarefa atribuida por administrador nao pode ser cancelada pelo comercial");
      return;
    }

    const confirm = await SwalWithTheme.confirm(
      "Confirmar cancelamento",
      "Deseja cancelar esta tarefa?",
      "Cancelar tarefa",
      "Voltar"
    );
    if (!confirm.isConfirmed) return;

    try {
      await removerTarefa(task.id);
      await carregarTarefas();
      SwalWithTheme.success("Sucesso", "Tarefa cancelada");
    } catch (error) {
      const message = error?.response?.data?.message || "Falha ao cancelar tarefa";
      SwalWithTheme.error("Erro", message);
    }
  };

  const statusOptionMap = useMemo(() => {
    const map = new Map();
    statusList.forEach((s) => map.set(String(s.id), s));
    return map;
  }, [statusList]);

  const parceirosMap = useMemo(() => {
    const map = new Map();
    parceiros.forEach((p) => map.set(String(p.id), p));
    return map;
  }, [parceiros]);

  const tiposMap = useMemo(() => {
    const map = new Map();
    tiposTarefa.forEach((tipo) => map.set(String(tipo.id), tipo));
    return map;
  }, [tiposTarefa]);

  const parceiroTaskOptions = useMemo(() => {
    const selectedIds = new Set((form.parceiroIds || []).map(String));
    const responsavelEdicaoId = editingTask
      ? String(form.usuarioIds[0] || editingTask?.idUsuario || "")
      : "";

    const baseParceiros =
      editingTask && responsavelEdicaoId
        ? parceiros.filter((partner) => {
          const partnerResponsavelId = String(partner?.usuarioId ?? partner?.usuario?.id ?? "");
          return partnerResponsavelId === responsavelEdicaoId || selectedIds.has(String(partner?.id ?? ""));
        })
        : parceiros;

    return baseParceiros.map((p) => ({ value: String(p.id), label: p.nome }));
  }, [editingTask, form.parceiroIds, form.usuarioIds, parceiros]);

  const motivosMap = useMemo(() => {
    const map = new Map();
    motivosList.forEach((m) => map.set(String(m.id), m));
    return map;
  }, [motivosList]);

  const getLinhaMotivoKey = (row) => String(row?.id ?? row?.idTarefa ?? row?.codigo ?? "");

  const getLinhaParceiroKey = (row) => String(row?.id ?? row?.idTarefa ?? row?.codigo ?? "");

  const toggleMotivosExpandidos = (row) => {
    const key = getLinhaMotivoKey(row);
    if (!key) return;
    setMotivosExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const bancosMap = useMemo(() => {
    const map = new Map();
    bancos.forEach((b) => {
      const key = String(b.bancoId ?? b.BancoId ?? "");
      if (key) map.set(key, b);
    });
    return map;
  }, [bancos]);

  const conveniosMap = useMemo(() => {
    const map = new Map();
    convenios.forEach((c) => {
      const key = String(c.convenioId ?? c.ConvenioId ?? "");
      if (key) map.set(key, c);
    });
    return map;
  }, [convenios]);

  const concorrentesMap = useMemo(() => {
    const map = new Map();
    concorrentes.forEach((concorrente) => {
      const key = String(concorrente?.id ?? "");
      if (key) map.set(key, concorrente);
    });
    return map;
  }, [concorrentes]);

  const carregarChecks = async (taskId) => {
    const data = await listarChecksTarefa(taskId);
    setChecks(Array.isArray(data) ? data : []);
  };

  const carregarHistoricoExecucao = async (taskId) => {
    if (!taskId) {
      setExecTaskHistory([]);
      return;
    }
    setExecTaskHistoryLoading(true);
    try {
      const data = await listarHistoricoTarefa(taskId);
      setExecTaskHistory(Array.isArray(data) ? data : []);
    } catch {
      setExecTaskHistory([]);
      SwalWithTheme.error("Erro", "Não foi possível carregar a timeline da tarefa");
    } finally {
      setExecTaskHistoryLoading(false);
    }
  };

  useEffect(() => {
    approvalNotificationRefreshRef.current = {
      isAdmin,
      execTaskId: execTask?.id || null,
      refreshTasks: () => carregarTarefas({ silent: true }),
      refreshPendingApprovals: () => carregarPendenciasAprovacao(),
      refreshChecks: (taskId) => carregarChecks(taskId),
    };
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleNotificationCreated = (event) => {
      const notification = event?.detail;
      if (!isApprovalNotification(notification)) {
        return;
      }

      const current = approvalNotificationRefreshRef.current;
      if (isApprovalPendingNotification(notification) && !current.isAdmin) {
        return;
      }

      const taskId = getNotificationTaskId(notification);
      if (approvalNotificationTimeoutRef.current) {
        window.clearTimeout(approvalNotificationTimeoutRef.current);
      }

      approvalNotificationTimeoutRef.current = window.setTimeout(() => {
        approvalNotificationTimeoutRef.current = null;
        void current.refreshTasks();

        if (current.isAdmin) {
          void current.refreshPendingApprovals();
        }

        if (current.execTaskId != null && taskId != null && String(current.execTaskId) === String(taskId)) {
          void current.refreshChecks(current.execTaskId);
        }
      }, 350);
    };

    window.addEventListener(APP_NOTIFICATION_CREATED_EVENT, handleNotificationCreated);
    return () => {
      window.removeEventListener(APP_NOTIFICATION_CREATED_EVENT, handleNotificationCreated);
      if (approvalNotificationTimeoutRef.current) {
        window.clearTimeout(approvalNotificationTimeoutRef.current);
        approvalNotificationTimeoutRef.current = null;
      }
    };
  }, []);

  const abrirExecucao = async (task) => {
    try {
      setExecLoading(true);
      setAcaoLoading(false);
      setExecTask(task);
      const dadosFinanceiros = parseJsonSafe(task.dadosFinanceiros, []);
      const bancosConvenios = mapDadosFinanceirosParaLinhas(dadosFinanceiros).map((linha) => ({
        ...createBancoLinha(),
        ...linha,
        bancoId: String(linha.bancoId || ""),
        convenioId: String(linha.convenioId || ""),
        valorProduzido: formatMoneyBRL(linha.valorProduzido),
        concorrenteId: String(linha.concorrenteId || ""),
        concorrenteOutro: String(linha.concorrenteOutro || ""),
      }));

      const defaultConcluirForm = {
        relatorio: task.relatorio || "",
        produto: task.produto || "",
        comissaoParceiroAjustada:
          typeof task.comissaoParceiroAjustada === "boolean" ? task.comissaoParceiroAjustada : null,
        loginParceiroSolicitado:
          typeof task.loginParceiroSolicitado === "boolean" ? task.loginParceiroSolicitado : null,
        bancosConvenios: bancosConvenios.length ? bancosConvenios : [createBancoLinha()],
      };
      const draft = loadConcluirDraft(task.id);
      setConcluirForm(
        draft
          ? {
            relatorio: draft.relatorio || defaultConcluirForm.relatorio,
            produto: draft.produto || defaultConcluirForm.produto,
            comissaoParceiroAjustada:
              typeof draft.comissaoParceiroAjustada === "boolean"
                ? draft.comissaoParceiroAjustada
                : defaultConcluirForm.comissaoParceiroAjustada,
            loginParceiroSolicitado:
              typeof draft.loginParceiroSolicitado === "boolean"
                ? draft.loginParceiroSolicitado
                : defaultConcluirForm.loginParceiroSolicitado,
            bancosConvenios: Array.isArray(draft.bancosConvenios) && draft.bancosConvenios.length
              ? draft.bancosConvenios.map((linha) => ({
                ...createBancoLinha(),
                ...linha,
                valorProduzido: formatMoneyBRL(linha?.valorProduzido),
              }))
              : defaultConcluirForm.bancosConvenios,
          }
          : defaultConcluirForm
      );
      setFallbackForm({ motivo: draft?.fallbackMotivo || "", files: [] });
      setEvidenciaForm({ observacao: draft?.evidenciaObservacao || "", files: [] });
      setFallbackLiberado({
        habilitado: false,
        acao: null,
        mensagem: "",
      });

      await Promise.all([
        carregarChecks(task.id),
        carregarHistoricoExecucao(task.id),
      ]);
      setShowExecModal(true);
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao abrir execucao da tarefa";
      SwalWithTheme.error("Erro", message);
    } finally {
      setExecLoading(false);
    }
  };

  useEffect(() => {
    const requestedTaskId = location.state?.openTaskId;
    const requestId = location.state?.openRequestId;

    if (!requestedTaskId) {
      notificationOpenRef.current = null;
      return;
    }

    const requestKey = `${requestedTaskId}:${requestId || "default"}`;
    if (notificationOpenRef.current === requestKey) {
      return;
    }
    notificationOpenRef.current = requestKey;

    let cancelled = false;

    (async () => {
      try {
        const task = await buscarTarefa(requestedTaskId);
        if (!task?.id) {
          throw new Error("Não foi possível localizar a tarefa da notificação");
        }
        if (cancelled) {
          return;
        }
        await abrirExecucao(task);
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            "Não foi possível abrir a tarefa selecionada";
          SwalWithTheme.error("Erro", message);
        }
      } finally {
        if (!cancelled) {
          navigate(location.pathname, { replace: true, state: null });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.state, navigate]);

  const tipoExecucao = useMemo(() => {
    if (!execTask) return null;
    return tiposTarefa.find((t) => String(t.id) === String(execTask.idTipoDeTarefa)) || null;
  }, [execTask, tiposTarefa]);

  const fallbackPolicy = useMemo(() => getStepPolicy(tipoExecucao, "fallback"), [tipoExecucao]);
  const evidencePolicy = useMemo(() => getStepPolicy(tipoExecucao, "evidence"), [tipoExecucao]);
  const checkinExigido = requiresCheckin(tipoExecucao);
  const checkoutExigido = requiresCheckout(tipoExecucao);
  const permiteFallback = isStepEnabled(fallbackPolicy);
  const fallbackExigeImagem = stepRequiresImage(fallbackPolicy);
  const fallbackExigeAprovacao = stepRequiresApproval(fallbackPolicy);
  const exigeEvidencia = isStepEnabled(evidencePolicy);
  const evidenciaExigeImagem = stepRequiresImage(evidencePolicy);
  const evidenciaExigeAprovacao = stepRequiresApproval(evidencePolicy);

  const checkinOk = useMemo(
    () => checks.some((c) => c.acao === "CHECKIN" && ["OK", "APROVADO"].includes(c.status)),
    [checks]
  );

  const checkoutOk = useMemo(
    () => checks.some((c) => c.acao === "CHECKOUT" && ["OK", "APROVADO"].includes(c.status)),
    [checks]
  );

  const evidenciaOk = useMemo(
    () => checks.some((c) => c.acao === "EVIDENCIA_CONCLUSAO" && ["OK", "APROVADO"].includes(c.status)),
    [checks]
  );

  const checkEvidencia = useMemo(
    () => checks.find((c) => c.acao === "EVIDENCIA_CONCLUSAO" && ["OK", "APROVADO", "PENDENTE_APROVACAO"].includes(c.status)),
    [checks]
  );

  const checkinPendente = useMemo(
    () => checks.some((c) => c.acao === "CHECKIN" && c.status === "PENDENTE_APROVACAO"),
    [checks]
  );

  const checkoutPendente = useMemo(
    () => checks.some((c) => c.acao === "CHECKOUT" && c.status === "PENDENTE_APROVACAO"),
    [checks]
  );

  const evidenciaPendente = useMemo(
    () => checks.some((c) => c.acao === "EVIDENCIA_CONCLUSAO" && c.status === "PENDENTE_APROVACAO"),
    [checks]
  );
  const execTaskGeoPartner = useMemo(
    () => buildTaskGeoPartner({ task: execTask, partnerLookup: parceirosMap }),
    [execTask, parceirosMap]
  );

  const motivosDaExecucao = useMemo(() => {
    if (!execTask) return [];
    const porIds = (execTask.motivosIds || []).map((id) => motivosMap.get(String(id))).filter(Boolean);
    if (porIds.length) return porIds;

    const raw = String(execTask.motivo || "");
    if (!raw.trim()) return [];

    const mapPorNome = new Map(
      motivosList.map((motivo) => [normalizeKey(motivo.nome), motivo])
    );
    return raw
      .split(",")
      .map((nome) => mapPorNome.get(normalizeKey(nome)))
      .filter(Boolean);
  }, [execTask, motivosMap, motivosList]);

  const fieldModes = useMemo(() => mergeFieldModes(motivosDaExecucao), [motivosDaExecucao]);
  const bancoFieldMode = fieldModes[TASK_FIELD_CODES.BANCO_NEGOCIADO];
  const produtoFieldMode = fieldModes[TASK_FIELD_CODES.PRODUTO_NEGOCIADO];
  const convenioFieldMode = fieldModes[TASK_FIELD_CODES.CONVENIO_NEGOCIADO];
  const valorFieldMode = fieldModes[TASK_FIELD_CODES.VALOR_PRODUZIDO];
  const concorrenteFieldMode = fieldModes[TASK_FIELD_CODES.CONCORRENTE];

  const exibirBancoNegociado = isFieldVisible(bancoFieldMode);
  const exibirProdutoNegociado = isFieldVisible(produtoFieldMode);
  const exibirConvenioNegociado = isFieldVisible(convenioFieldMode);
  const exibirValorProduzido = isFieldVisible(valorFieldMode);
  const exibirConcorrente = isFieldVisible(concorrenteFieldMode);

  const bancoObrigatorio = isFieldRequired(bancoFieldMode);
  const produtoObrigatorio = isFieldRequired(produtoFieldMode);
  const convenioObrigatorio = isFieldRequired(convenioFieldMode);
  const valorObrigatorio = isFieldRequired(valorFieldMode);
  const concorrenteObrigatorio = isFieldRequired(concorrenteFieldMode);

  const algumCampoFinanceiroHabilitado = hasVisibleField(fieldModes, [
    TASK_FIELD_CODES.BANCO_NEGOCIADO,
    TASK_FIELD_CODES.PRODUTO_NEGOCIADO,
    TASK_FIELD_CODES.CONVENIO_NEGOCIADO,
    TASK_FIELD_CODES.VALOR_PRODUZIDO,
    TASK_FIELD_CODES.CONCORRENTE,
  ]);
  const algumCampoLinhaHabilitado = hasVisibleField(fieldModes, [
    TASK_FIELD_CODES.BANCO_NEGOCIADO,
    TASK_FIELD_CODES.CONVENIO_NEGOCIADO,
    TASK_FIELD_CODES.VALOR_PRODUZIDO,
    TASK_FIELD_CODES.CONCORRENTE,
  ]);

  const conveniosOptions = useMemo(
    () =>
      convenios
        .map((c) => ({
          value: String(c.convenioId || c.ConvenioId || ""),
          label: c.convenioNome || c.ConvenioNome || String(c.convenioId || c.ConvenioId || ""),
        }))
        .filter((option) => option.value),
    [convenios]
  );

  const concorrentesOptions = useMemo(() => {
    const options = (concorrentes || [])
      .map((concorrente) => ({
        value: String(concorrente.id || ""),
        label: concorrente.nome || String(concorrente.id || ""),
      }))
      .filter((option) => option.value);
    return [...options, { value: "OUTRO", label: "Outro" }];
  }, [concorrentes]);

  const handleGeoCheckConfirm = async ({ action, geo }) => {
    if (!execTask || acaoLoading) return;
    try {
      setAcaoLoading(true);
      if (action === "checkin") {
        await checkinTarefa(execTask.id, geo);
      } else {
        await checkoutTarefa(execTask.id, geo);
      }
      await carregarChecks(execTask.id);
      await carregarHistoricoExecucao(execTask.id);
      setFallbackLiberado({
        habilitado: false,
        acao: null,
        mensagem: "",
      });
      setGeoCheckDrawer((current) => ({ ...current, open: false }));
      SwalWithTheme.success("Sucesso", action === "checkin" ? "Check-in registrado" : "Check-out registrado");
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Falha ao executar check";
      SwalWithTheme.error("Erro", message);
      if (permiteFallback) {
        const geoInfo = parseGeoErrorInfo(message);
        const mensagemFallback = geoInfo?.distancia
          ? `Distancia atual ${geoInfo.distancia} m${geoInfo.raio ? ` (raio maximo ${geoInfo.raio} m)` : ""}.`
          : message;
        setFallbackLiberado({
          habilitado: true,
          acao: action,
          mensagem: mensagemFallback,
        });
        setFallbackForm((prev) => {
          if (prev.motivo?.trim()) return prev;
          const base = geoInfo?.distancia
            ? `Tentativa fora do raio. Distancia atual ${geoInfo.distancia} m${geoInfo.raio ? ` e raio maximo ${geoInfo.raio} m` : ""}.`
            : "Tentativa de check por geolocalizacao sem sucesso.";
          return { ...prev, motivo: base };
        });
        alert.warning("Check com falha", "Fallback liberado para essa acao apos a falha no check.", {
          position: "top-right",
          duration: 4200,
        });
      }
    } finally {
      setAcaoLoading(false);
    }
  };

  const handleSubmitFallback = async ({ action } = {}) => {
    if (!execTask || acaoLoading || fallbackSubmitInFlightRef.current) return;
    const acao = action || fallbackLiberado.acao;
    const motivo = (fallbackForm.motivo || "").trim();
    const files = Array.isArray(fallbackForm.files) ? fallbackForm.files : [];

    if (!motivo) {
      SwalWithTheme.error("Erro", "Informe o motivo do fallback");
      return;
    }
    if (!fallbackLiberado.habilitado || !acao) {
      SwalWithTheme.error("Erro", "Fallback so fica disponivel apos falha no check");
      return;
    }
    if (fallbackExigeImagem && files.length === 0) {
      SwalWithTheme.error("Erro", "Esse fallback exige ao menos uma imagem");
      return;
    }

    try {
      fallbackSubmitInFlightRef.current = true;
      setAcaoLoading(true);
      const payload = { motivo, files };
      if (acao === "checkin") {
        await fallbackCheckinTarefa(execTask.id, payload);
      } else {
        await fallbackCheckoutTarefa(execTask.id, payload);
      }
      await carregarChecks(execTask.id);
      await carregarHistoricoExecucao(execTask.id);
      await carregarPendenciasAprovacao();
      SwalWithTheme.success(
        "Sucesso",
        fallbackExigeAprovacao
          ? "Fallback enviado e aguardando aprovacao do admin"
          : "Fallback registrado com sucesso"
      );
      setGeoCheckDrawer((current) => ({ ...current, open: false }));
      setFallbackForm((prev) => ({ ...prev, motivo: "", files: [] }));
      setFallbackLiberado({
        habilitado: false,
        acao: null,
        mensagem: "",
      });
    } catch (error) {
      const message = error?.response?.data?.message || "Falha ao enviar fallback";
      SwalWithTheme.error("Erro", message);
    } finally {
      fallbackSubmitInFlightRef.current = false;
      setAcaoLoading(false);
    }
  };

  useEffect(() => {
    if (!showExecModal) {
      setGeoCheckDrawer((current) => ({ ...current, open: false }));
    }
  }, [showExecModal]);

  const enviarEvidencia = async () => {
    if (!execTask || acaoLoading) return;
    const observacao = (evidenciaForm.observacao || "").trim();
    const files = Array.isArray(evidenciaForm.files) ? evidenciaForm.files : [];
    if (evidenciaExigeImagem && files.length === 0) {
      SwalWithTheme.error("Erro", "Esse tipo exige ao menos uma imagem na evidencia");
      return;
    }

    try {
      setAcaoLoading(true);
      await evidenciarTarefa(execTask.id, {
        observacao,
        files,
      });
      await carregarChecks(execTask.id);
      await carregarHistoricoExecucao(execTask.id);
      await carregarPendenciasAprovacao();
      SwalWithTheme.success(
        "Sucesso",
        evidenciaExigeAprovacao
          ? "Evidencia enviada e aguardando aprovacao do admin"
          : "Evidencia registrada com sucesso"
      );
      setEvidenciaForm({ observacao: "", files: [] });
    } catch (error) {
      const message = error?.response?.data?.message || "Falha ao enviar evidencia";
      SwalWithTheme.error("Erro", message);
    } finally {
      setAcaoLoading(false);
    }
  };

  const decidirPendencia = async (pendencia, aprovar) => {
    const modal = await SwalWithTheme.fire({
      title: aprovar ? "Aprovar check pendente?" : "Reprovar check pendente?",
      text: "Opcional: informe observacao para auditoria",
      input: "text",
      inputPlaceholder: "Observacao",
      showCancelButton: true,
      confirmButtonText: aprovar ? "Aprovar" : "Reprovar",
      cancelButtonText: "Cancelar",
      icon: aprovar ? "question" : "warning",
    });

    if (!modal.isConfirmed) return;

    try {
      setAprovarLoadingId(pendencia.id);
      if (aprovar) {
        await aprovarCheckTarefa(pendencia.id, modal.value);
      } else {
        await reprovarCheckTarefa(pendencia.id, modal.value);
      }
      await carregarPendenciasAprovacao();
      if (execTask && String(execTask.id) === String(pendencia.tarefaId)) {
        await carregarChecks(execTask.id);
        await carregarHistoricoExecucao(execTask.id);
      }
      SwalWithTheme.success("Sucesso", aprovar ? "Check aprovado" : "Check reprovado");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao decidir pendencia";
      SwalWithTheme.error("Erro", message);
    } finally {
      setAprovarLoadingId(null);
    }
  };

  const montarDadosFinanceiros = () => {
    return (concluirForm.bancosConvenios || [])
      .map((linha) => ({
        bancoId: String(linha?.bancoId || "").trim(),
        convenioId: String(linha?.convenioId || "").trim(),
        valorProduzido: String(linha?.valorProduzido || "").trim(),
        concorrenteId: String(linha?.concorrenteId || "").trim(),
        concorrenteOutro: String(linha?.concorrenteOutro || "").trim(),
      }))
      .filter((linha) =>
        linha.bancoId
        || linha.convenioId
        || linha.valorProduzido
        || linha.concorrenteId
        || linha.concorrenteOutro
      )
      .map((linha) => {
        const banco = bancosMap.get(linha.bancoId);
        const convenio = conveniosMap.get(linha.convenioId);
        const concorrente = concorrentesMap.get(linha.concorrenteId);

        const valorNumero = parseMoneyBRL(linha.valorProduzido);

        return {
          bancoId: banco?.bancoId || linha.bancoId || null,
          bancoNome: banco?.bancoNome || linha.bancoId || null,
          convenioId: convenio?.convenioId || convenio?.ConvenioId || linha.convenioId || null,
          convenioNome: convenio?.convenioNome || convenio?.ConvenioNome || linha.convenioId || null,
          valorProduzido: Number.isFinite(valorNumero) ? valorNumero : null,
          concorrenteId: linha.concorrenteId && linha.concorrenteId !== "OUTRO" ? linha.concorrenteId : null,
          concorrenteNome:
            linha.concorrenteId === "OUTRO"
              ? "Outro"
              : (concorrente?.nome || (linha.concorrenteId || null)),
          concorrenteOutro: linha.concorrenteId === "OUTRO" ? (linha.concorrenteOutro || null) : null,
        };
      });
  };

  const concluirExecucao = async () => {
    if (!execTask || acaoLoading) return;
    if (!podeConcluir(execTask)) {
      SwalWithTheme.error("Acao nao permitida", "Essa tarefa ja foi concluida/cancelada ou nao pertence ao seu perfil.");
      return;
    }
    const relatorio = (concluirForm.relatorio || "").trim();
    const produto = (concluirForm.produto || "").trim();
    if (produtoObrigatorio && !produto) {
      SwalWithTheme.error("Erro", "Campo Produto negociado e obrigatorio");
      return;
    }

    const dadosFinanceiros = montarDadosFinanceiros();
    const exigeLinhasFinanceiras = bancoObrigatorio || convenioObrigatorio || valorObrigatorio || concorrenteObrigatorio;
    if (exigeLinhasFinanceiras && dadosFinanceiros.length === 0) {
      SwalWithTheme.error("Erro", "Preencha ao menos uma linha de Banco/Convenio/Valor para concluir");
      return;
    }

    const linhaSemBanco = dadosFinanceiros.some((linha) => !linha.bancoId);
    if (bancoObrigatorio && linhaSemBanco) {
      SwalWithTheme.error("Erro", "Campo Banco e obrigatorio em todas as linhas");
      return;
    }

    const linhaSemConvenio = dadosFinanceiros.some((linha) => !linha.convenioId);
    if (convenioObrigatorio && linhaSemConvenio) {
      SwalWithTheme.error("Erro", "Campo Convenio e obrigatorio em todas as linhas");
      return;
    }

    const linhaComValorInvalido = dadosFinanceiros.some((linha) => {
      if (linha.valorProduzido == null) return Boolean(valorObrigatorio);
      const value = Number(linha.valorProduzido);
      return !Number.isFinite(value) || value <= 0;
    });
    if ((valorObrigatorio || exibirValorProduzido) && linhaComValorInvalido) {
      SwalWithTheme.error(
        "Erro",
        valorObrigatorio
          ? "Campo Valor deve ser maior que zero em todas as linhas"
          : "Se informado, o campo Valor deve ser maior que zero."
      );
      return;
    }

    const linhaSemConcorrente = dadosFinanceiros.some((linha) => !String(linha.concorrenteNome || linha.concorrenteId || "").trim());
    if (concorrenteObrigatorio && linhaSemConcorrente) {
      SwalWithTheme.error("Erro", "Campo Concorrente e obrigatorio em todas as linhas");
      return;
    }

    const linhaOutroSemDescricao = dadosFinanceiros.some(
      (linha) => String(linha.concorrenteNome || "").toUpperCase() === "OUTRO" && !String(linha.concorrenteOutro || "").trim()
    );
    if (exibirConcorrente && linhaOutroSemDescricao) {
      SwalWithTheme.error("Erro", "Ao selecionar concorrente 'Outro', informe o nome do concorrente");
      return;
    }

    try {
      setAcaoLoading(true);
      await concluirTarefaComPayload(execTask.id, {
        relatorio,
        dadosFinanceiros: dadosFinanceiros.length ? JSON.stringify(dadosFinanceiros) : null,
        produto: produto || null,
        tipoProduto: null,
        comissaoParceiroAjustada: concluirForm.comissaoParceiroAjustada,
        loginParceiroSolicitado: concluirForm.loginParceiroSolicitado,
      });

      fireTaskCompletedConfetti();
      SwalWithTheme.success("Sucesso", "Tarefa concluida com sucesso");
      clearConcluirDraft(execTask.id);
      setShowExecModal(false);
      setExecTask(null);
      setChecks([]);
      setExecTaskHistory([]);
      await carregarTarefas();
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao concluir tarefa";
      SwalWithTheme.error("Erro", message);
    } finally {
      setAcaoLoading(false);
    }
  };

  const columns = [
    {
      key: "idTipoDeTarefa",
      header: "Tipo",
      mobilePrimary: true,
      mobileOrder: 0,
      mobileSlot: "title",
      render: (value, row) => {
        if (metaLoading) return <Skeleton className="h-4 w-28" />;
        const tipo = tiposMap.get(String(value || row?.tipoDeTarefa?.id || ""));
        const iconDef = resolveTaskTypeIconDefinition(tipo);
        const Icon = iconDef.icon;
        return (
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-border bg-muted/30 text-foreground">
              <Icon className="h-4 w-4" />
            </span>
            <span className="truncate font-medium text-foreground">{tipo?.nomeTipo || "N/A"}</span>
          </div>
        );
      },
    },
    {
      key: "idUsuario",
      header: "Responsavel",
      mobileSpan: 2,
      mobileOrder: 1,
      mobileSlot: "footer",
      mobileRender: (_, row) => {
        const nome = row?.usuarioNome || "Sem responsável";
        return (
          <span className="flex min-w-0 items-center gap-2">
            <Avatar className="h-6 w-6 border border-border/70">
              <AvatarImage src={row?.usuarioFotoPerfilUrl} alt={nome} />
              <AvatarFallback className="bg-muted text-[10px] text-foreground">{getAvatarInitials(nome)}</AvatarFallback>
            </Avatar>
            <span className="truncate text-sm text-muted-foreground">{nome}</span>
          </span>
        );
      },
      render: (_, row) => {
        const nome = row?.usuarioNome || "Sem responsavel";
        return (
          <div className="flex min-w-0 items-center gap-3">
            <Avatar className="h-10 w-10 border border-border/70">
              <AvatarImage src={row?.usuarioFotoPerfilUrl} alt={nome} />
              <AvatarFallback className="bg-muted text-foreground">
                {getAvatarInitials(nome)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground">{nome}</p>
              {!row?.usuarioNome ? (
                <p className="truncate text-xs text-muted-foreground">Nao atribuido</p>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      key: "parceirosIds",
      header: "Parceiros",
      mobileSpan: 2,
      mobileOrder: 3,
      mobileSlot: "meta",
      render: (_, row) => {
        const ids = row.parceirosIds?.length ? row.parceirosIds.map((id) => String(id)) : [];
        const nomes = Array.isArray(row.parceirosNomes)
          ? row.parceirosNomes.map((nome) => String(nome || "").trim()).filter(Boolean)
          : [];
        if (!ids.length && !nomes.length) return "Sem parceiro";
        if (metaLoading && !nomes.length) return <Skeleton className="h-4 w-36" />;

        const parceirosInfo = ids.length
          ? ids.map((id, index) => ({ id, nome: nomes[index] || parceirosMap.get(id)?.nome || `Parceiro ${id}` }))
          : nomes.map((nome) => ({ id: null, nome }));
        // Duas etiquetas e o resto num painel: expandir 61 parceiros dentro da celula quebrava a
        // tabela, e ninguem le 61 nomes empilhados numa linha.
        const visiveis = parceirosInfo.slice(0, 2);
        const extra = parceirosInfo.length - visiveis.length;

        return (
          <div className="flex flex-wrap items-center gap-1.5">
            {visiveis.map((parceiro, index) => (
              <button
                key={`parceiro-task-${row.id}-${parceiro.id ?? index}`}
                type="button"
                disabled={!parceiro.id}
                onClick={(event) => {
                  event.stopPropagation();
                  if (parceiro.id) navigate(`/parceiros/${parceiro.id}`);
                }}
                className="inline-flex max-w-[180px] items-center truncate rounded-full border border-border bg-background/75 px-2 py-0.5 text-xs font-medium text-foreground transition hover:border-primary/35 hover:bg-primary/10 disabled:cursor-default disabled:hover:border-border disabled:hover:bg-background/75 dark:bg-muted/50"
                title={parceiro.id ? "Abrir detalhes do parceiro" : parceiro.nome}
              >
                {parceiro.nome}
              </button>
            ))}
            {extra > 0 ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setBuscaPainelParceiros("");
                  setPainelParceiros({
                    aberto: true,
                    titulo: `${parceirosInfo.length} parceiros nesta tarefa`,
                    itens: parceirosInfo,
                  });
                }}
                className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground transition hover:text-foreground"
                title={`Ver os ${parceirosInfo.length} parceiros`}
              >
                +{extra}
              </button>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "dataInicio",
      header: "Data",
      mobileSpan: 2,
      mobileOrder: 2,
      mobileSlot: "subtitle",
      mobileRender: (value) => (value
        ? new Date(value).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
        : null),
      render: (value) => (value ? new Date(value).toLocaleString("pt-BR") : "N/A"),
    },
    {
      key: "motivo",
      header: "Motivos",
      mobileSpan: 2,
      mobileOrder: 4,
      mobileSlot: "meta",
      render: (_, row) => {
        if (metaLoading) return <Skeleton className="h-4 w-44" />;
        const nomesPorId = (row?.motivosIds || [])
          .map((id) => motivosMap.get(String(id))?.nome)
          .filter(Boolean);
        const motivos = nomesPorId.length ? nomesPorId : parseMotivos(row?.motivo);
        if (!motivos.length) return "N/A";
        const linhaExpandida = motivosExpandidos.has(getLinhaMotivoKey(row));
        const visiveis = linhaExpandida ? motivos : motivos.slice(0, MOTIVOS_VISIBLE_BUDGET);
        const extra = motivos.length - visiveis.length;
        return (
          <div className="flex flex-wrap gap-1.5" title={motivos.join(", ")}>
            {visiveis.map((motivo) => (
              <span
                key={`motivo-task-${row.id}-${motivo}`}
                className="inline-flex items-center rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs font-medium"
              >
                {motivo}
              </span>
            ))}
            {extra > 0 && !linhaExpandida && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleMotivosExpandidos(row);
                }}
                className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground hover:border-primary/60 hover:text-foreground"
                title={`Ver todos os motivos (${motivos.length})`}
                aria-label={`Ver todos os motivos (${motivos.length})`}
              >
                +{extra}
              </button>
            )}
            {linhaExpandida && motivos.length > MOTIVOS_VISIBLE_BUDGET && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleMotivosExpandidos(row);
                }}
                className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground hover:border-primary/60 hover:text-foreground"
              >
                ver menos
              </button>
            )}
          </div>
        );
      },
    },
    {
      key: "idStatusAtividade",
      header: "Status",
      mobileSpan: 1,
      mobileOrder: 1,
      mobileSlot: "badge",
      render: (value, row) => {
        if (metaLoading) return <Skeleton className="h-6 w-24 rounded-full" />;
        const status = statusOptionMap.get(String(value));
        return <StatusBadge status={status?.nome || row?.statusNome || "N/A"} variant={status?.cor || "default"} />;
      },
    },
  ];

  const actions = [
    { icon: CheckCircle2, title: "Concluir", onClick: (row) => abrirExecucao(row) },
    { icon: UserRoundPen, title: "Editar", onClick: (row) => handleEditar(row), disabled: (row) => !podeEditar(row) },
    { icon: Trash2, title: "Cancelar", onClick: (row) => handleRemover(row), disabled: (row) => !podeCancelar(row) },
  ];

  const parceiroFilterOptions = parceiros.map((p) => ({ value: String(p.id), label: p.nome }));
  const responsavelOptions = usuariosResponsaveis.map((user) => ({
    value: String(user.id),
    label: user.nome,
  }));
  const statusFilterOptions = statusList.map((s) => ({ value: String(s.id), label: s.nome }));
  const tipoFilterOptions = tiposTarefa.map((t) => ({ value: String(t.id), label: t.nomeTipo }));
  const motivosOptions = motivosList.map((m) => ({ value: String(m.id), label: m.nome }));
  const tiposOptions = tiposTarefa.map((t) => ({ value: String(t.id), label: t.nomeTipo }));
  const bancosOptions = bancos
    .map((b) => ({
      value: String(b.bancoId ?? b.BancoId ?? ""),
      label: b.bancoNome ?? b.BancoNome ?? String(b.bancoId ?? b.BancoId ?? ""),
    }))
    .filter((option) => option.value);

  const labelAcaoCheck = (acao) => {
    if (acao === "CHECKIN") return "Check-in";
    if (acao === "CHECKOUT") return "Check-out";
    if (acao === "EVIDENCIA_CONCLUSAO") return "Evidencia";
    return acao || "N/A";
  };

  const getPendenciaTextoLabel = (pendencia) => {
    if (pendencia?.acao === "EVIDENCIA_CONCLUSAO") return "Observacao informada";
    if (pendencia?.modo === "FALLBACK") return "Motivo informado";
    return "Texto informado";
  };

  const formatDateTimeLabel = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleString("pt-BR");
  };

  const getTaskHistoryIcon = (categoria) => {
    switch (categoria) {
      case "parceiro":
        return UserRoundPen;
      case "vinculo":
        return Plus;
      case "execucao":
        return CheckCircle2;
      case "aprovacao":
        return ShieldCheck;
      case "alerta":
        return AlertTriangle;
      case "tarefa":
      default:
        return CalendarClock;
    }
  };

  const getTaskHistoryToneClasses = (categoria) => {
    switch (categoria) {
      case "parceiro":
        return "border-info/25 bg-info/10 text-info";
      case "tarefa":
        return "border-primary/25 bg-primary/10 text-primary";
      case "vinculo":
        return "border-gold/25 bg-gold/10 text-gold";
      case "execucao":
        return "border-success/25 bg-success/10 text-success";
      case "aprovacao":
        return "border-info/25 bg-info/10 text-info";
      case "alerta":
        return "border-destructive/25 bg-destructive/10 text-destructive";
      default:
        return "border-border bg-muted/25 text-muted-foreground";
    }
  };

  const formatMotivosLabel = (row) => {
    const nomes = (row?.motivosIds || [])
      .map((id) => motivosMap.get(String(id))?.nome)
      .filter(Boolean);
    const motivos = nomes.length ? nomes : parseMotivos(row?.motivo);
    if (!motivos.length) return "N/A";
    const visiveis = motivos.slice(0, MOTIVOS_VISIBLE_BUDGET);
    const extra = motivos.length - visiveis.length;
    return `${visiveis.join(", ")}${extra > 0 ? ` +${extra}` : ""}`;
  };

  const activeFilterCount =
    filterStatusIds.length +
    filterParceiroIds.length +
    filterResponsavelIds.length +
    filterTipoIds.length +
    (filtroDataInicio && filtroDataInicio !== defaultDateRange.from ? 1 : 0) +
    (filtroDataFim && filtroDataFim !== defaultDateRange.to ? 1 : 0);

  // Filtros abrem pelo funil da barra superior, no painel lateral padrão (FiltersSheet).
  usePageFilters({ count: activeFilterCount, onOpen: () => setFiltersOpen(true) });

  const handleClearFilters = () => {
    setFilterStatusIds([]);
    setFilterParceiroIds([]);
    setFilterResponsavelIds([]);
    setFilterTipoIds([]);
    setFiltroDataInicio(defaultDateRange.from);
    setFiltroDataFim(defaultDateRange.to);
    setFiltersOpen(false);
    setPage(0);
  };

  const tarefaModalProgress = calcProgress([
    Array.isArray(form.tipoIds) && form.tipoIds.length > 0,
    vinculoSomenteUsuario
      ? (isAdmin ? form.usuarioIds.length > 0 : true)
      : (Array.isArray(form.parceiroIds) && form.parceiroIds.length > 0),
    Array.isArray(form.motivoIds) && form.motivoIds.length > 0,
    Boolean(String(form.dataHora || "").trim()),
    String(form.observacoes || "").trim().length >= 15,
  ]);

  const conclusaoProgress = useMemo(() => {
    if (!execTask) return 0;
    const relatorioOk = String(concluirForm.relatorio || "").trim().length >= 1;
    const produtoOk = !produtoObrigatorio || Boolean(String(concluirForm.produto || "").trim());
    const checkinOkOrPendente = !checkinExigido || checkinOk || checkinPendente;
    const checkoutOkOrPendente = !checkoutExigido || checkoutOk || checkoutPendente;
    const evidenciaOkOrPendente = !exigeEvidencia || evidenciaOk || evidenciaPendente;

    const linhasDigitadas = (concluirForm.bancosConvenios || [])
      .map((linha) => ({
        bancoId: String(linha?.bancoId || "").trim(),
        convenioId: String(linha?.convenioId || "").trim(),
        valorProduzido: String(linha?.valorProduzido || "").trim(),
        concorrenteId: String(linha?.concorrenteId || "").trim(),
        concorrenteOutro: String(linha?.concorrenteOutro || "").trim(),
      }))
      .filter((linha) =>
        linha.bancoId
        || linha.convenioId
        || linha.valorProduzido
        || linha.concorrenteId
        || linha.concorrenteOutro
      );

    let financeiroOk = true;
    if (algumCampoLinhaHabilitado) {
      if ((bancoObrigatorio || convenioObrigatorio || valorObrigatorio || concorrenteObrigatorio) && linhasDigitadas.length === 0) {
        financeiroOk = false;
      } else {
        financeiroOk = linhasDigitadas.every((linha) => {
          if (bancoObrigatorio && !linha.bancoId) return false;
          if (convenioObrigatorio && !linha.convenioId) return false;
          const rawValor = String(linha.valorProduzido || "").trim();
          if (valorObrigatorio || rawValor) {
            const valorNumero = parseMoneyBRL(rawValor);
            if (valorNumero == null || !Number.isFinite(valorNumero) || valorNumero <= 0) return false;
          }
          if (concorrenteObrigatorio && !linha.concorrenteId) return false;
          if (exibirConcorrente && linha.concorrenteId.toUpperCase() === "OUTRO" && !linha.concorrenteOutro) return false;
          return true;
        });
      }
    }

    const fallbackOk = !fallbackLiberado.habilitado || Boolean(String(fallbackForm.motivo || "").trim());

    return calcProgress([
      relatorioOk,
      produtoOk,
      checkinOkOrPendente,
      checkoutOkOrPendente,
      evidenciaOkOrPendente,
      !algumCampoLinhaHabilitado || financeiroOk,
      fallbackOk,
    ]);
  }, [
    execTask,
    concluirForm,
    produtoObrigatorio,
    checkinExigido,
    checkinOk,
    checkinPendente,
    checkoutExigido,
    checkoutOk,
    checkoutPendente,
    exigeEvidencia,
    evidenciaOk,
    evidenciaPendente,
    algumCampoLinhaHabilitado,
    bancoObrigatorio,
    convenioObrigatorio,
    valorObrigatorio,
    exibirConcorrente,
    concorrenteObrigatorio,
    fallbackLiberado.habilitado,
    fallbackForm.motivo,
  ]);

  return (
    <div className="space-y-4">


      {isAdmin && (
        <section className="rounded-xl border border-border bg-card p-3 shadow-sm sm:p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-semibold">Pendencias de aprovacao</h2>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={carregarPendenciasAprovacao}
              disabled={pendenciasLoading}
            >
              {pendenciasLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Atualizar
            </Button>
          </div>

          {pendenciasLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando pendencias...
            </div>
          ) : pendenciasAprovacao.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">Nenhuma pendencia aguardando aprovacao.</p>
          ) : (
            <div className="space-y-2">
              {pendenciasAprovacao.map((pendencia) => {
                const estaSalvando = String(aprovarLoadingId || "") === String(pendencia.id);
                const parceirosResumo = Array.isArray(pendencia.parceirosNomes) ? pendencia.parceirosNomes.filter(Boolean) : [];
                return (
                  <div key={`pend-${pendencia.id}`} className="rounded-xl border border-border p-4">
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <StatusBadge status="Pendente aprovacao" variant="warning" />
                          <span className="text-xs text-muted-foreground">
                            Check #{pendencia.id} - Tarefa #{pendencia.tarefaId}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">
                            {pendencia.tarefaDescricao || "Tarefa sem descricao"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {labelAcaoCheck(pendencia.acao)} via {pendencia.modo || "N/A"}
                          </p>
                        </div>
                      </div>
                      {pendencia.responsavelNome ? (
                        <div className="flex min-w-[220px] items-center gap-3 rounded-2xl border border-border/70 bg-background/40 px-3 py-2">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={pendencia.responsavelFotoPerfilUrl} alt={pendencia.responsavelNome} />
                            <AvatarFallback>{getAvatarInitials(pendencia.responsavelNome)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="truncate text-xs text-muted-foreground">Responsavel</p>
                            <p className="truncate text-sm font-medium text-foreground">{pendencia.responsavelNome}</p>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div className="mb-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-2xl border border-border/70 bg-background/40 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Executor</p>
                        <p className="mt-1 text-sm text-foreground">{pendencia.usuarioNome || `ID ${pendencia.usuarioId || "-"}`}</p>
                      </div>
                      <div className="rounded-2xl border border-border/70 bg-background/40 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Tipo</p>
                        <p className="mt-1 text-sm text-foreground">{pendencia.tipoTarefaNome || "N/A"}</p>
                      </div>
                      <div className="rounded-2xl border border-border/70 bg-background/40 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Data da tarefa</p>
                        <p className="mt-1 text-sm text-foreground">{formatDateTimeLabel(pendencia.tarefaDataInicio)}</p>
                      </div>
                      <div className="rounded-2xl border border-border/70 bg-background/40 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Enviado em</p>
                        <p className="mt-1 text-sm text-foreground">{formatDateTimeLabel(pendencia.createdAt)}</p>
                      </div>
                    </div>

                    <div className="mb-3 grid gap-3 xl:grid-cols-[1.4fr,1fr]">
                      <div className="rounded-2xl border border-border/70 bg-background/40 px-3 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Parceiros</p>
                        <p className="mt-1 text-sm text-foreground">
                          {parceirosResumo.length ? parceirosResumo.join(", ") : "Sem parceiro vinculado"}
                        </p>
                      </div>
                      <div className="rounded-2xl border border-border/70 bg-background/40 px-3 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Observacoes da tarefa</p>
                        <p className="mt-1 text-sm text-foreground">
                          {pendencia.tarefaObservacoes || "Sem observacoes adicionais"}
                        </p>
                      </div>
                    </div>

                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <StatusBadge status="Pendente aprovacao" variant="warning" />
                      <span className="text-xs text-muted-foreground">
                        Modo: {pendencia.modo || "N/A"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Acao: {labelAcaoCheck(pendencia.acao)}
                      </span>
                    </div>
                    {pendencia.motivoFallback ? (
                      <div className="mb-3 rounded-2xl border border-border/70 bg-background/40 px-3 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          {getPendenciaTextoLabel(pendencia)}
                        </p>
                        <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{pendencia.motivoFallback}</p>
                      </div>
                    ) : null}
                    {Array.isArray(pendencia.fotoUrls) && pendencia.fotoUrls.length > 0 ? (
                      <div className="mb-3">
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          Imagens anexadas
                        </p>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                          {pendencia.fotoUrls.map((fotoUrl, fotoIdx) => {
                            const src = resolveCheckFileUrl(fotoUrl);
                            if (!src) return null;
                            return (
                              <a
                                key={`pend-${pendencia.id}-foto-${fotoIdx}`}
                                href={src}
                                target="_blank"
                                rel="noreferrer"
                                className="overflow-hidden rounded-md border border-border/70"
                                title="Abrir imagem"
                              >
                                <img
                                  src={src}
                                  alt={`Evidencia ${fotoIdx + 1}`}
                                  className="h-24 w-full object-cover"
                                  loading="lazy"
                                />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={estaSalvando}
                        onClick={() => decidirPendencia(pendencia, true)}
                      >
                        {estaSalvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        Aprovar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={estaSalvando}
                        onClick={() => decidirPendencia(pendencia, false)}
                      >
                        {estaSalvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                        Reprovar
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      <section className="rounded-xl border border-border bg-card p-3 shadow-sm sm:p-4">
        {/* Ações e busca na mesma linha; empilham só em tela estreita. */}
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center">
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button variant="outline" onClick={openCreateModal}>
              <Plus className="h-4 w-4" />
              Criar tarefa
            </Button>
          </div>
          <div className="w-full md:max-w-[520px]">
            <SearchBar
              placeholder="Pesquisar tarefa..."
              value={searchTerm}
              onChange={(e) => {
                setPage(0);
                setSearchTerm(e.target.value);
              }}
              className="w-full"
            />
          </div>
          <FiltersSheet
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            description="Aplicados à lista de tarefas."
            onClear={handleClearFilters}
            clearDisabled={activeFilterCount === 0}
          >
                  <div className="space-y-3">
                    <MultiSelectField
                      label="Status"
                      options={statusFilterOptions}
                      selectedValues={filterStatusIds}
                      onChange={(values) => {
                        setPage(0);
                        setFilterStatusIds(values.map(String));
                      }}
                      placeholder="Selecione status"
                      searchPlaceholder="Buscar status..."
                      emptyText="Nenhum status disponivel"
                    />
                    <MultiSelectField
                      label="Parceiro"
                      options={parceiroFilterOptions}
                      selectedValues={filterParceiroIds}
                      onChange={(values) => {
                        setPage(0);
                        setFilterParceiroIds(values.map(String));
                      }}
                      placeholder="Selecione parceiros"
                      searchPlaceholder="Buscar parceiro..."
                      emptyText="Nenhum parceiro disponivel"
                    />
                    {isAdmin ? (
                      <MultiSelectField
                        label="Responsavel"
                        options={responsavelOptions}
                        selectedValues={filterResponsavelIds}
                        onChange={(values) => {
                          setPage(0);
                          setFilterResponsavelIds(values.map(String));
                        }}
                        placeholder="Selecione responsaveis"
                        searchPlaceholder="Buscar responsavel..."
                        emptyText="Nenhum responsavel disponivel"
                      />
                    ) : null}
                    <MultiSelectField
                      label="Tipo"
                      options={tipoFilterOptions}
                      selectedValues={filterTipoIds}
                      onChange={(values) => {
                        setPage(0);
                        setFilterTipoIds(values.map(String));
                      }}
                      placeholder="Selecione tipos"
                      searchPlaceholder="Buscar tipo..."
                      emptyText="Nenhum tipo disponivel"
                    />
                    <DateRangeField
                      label="Periodo (entre)"
                      from={filtroDataInicio}
                      to={filtroDataFim}
                      onFromChange={(e) => {
                        setPage(0);
                        setFiltroDataInicio(e.target.value);
                      }}
                      onToChange={(e) => {
                        setPage(0);
                        setFiltroDataFim(e.target.value);
                      }}
                    />
                  </div>
          </FiltersSheet>
        </div>

        <DataTable
          columns={columns}
          data={tarefasFiltradas}
          actions={actions}
          loading={loading}
          pagination={taskPage}
          onPageChange={(target) => setPage(target)}
          onPageSizeChange={(size) => {
            setPage(0);
            setPageSize(size);
            setTaskPage((prev) => ({ ...prev, size }));
          }}
          enablePagination
        />
      </section>

      <Sheet open={painelParceiros.aberto} onOpenChange={(aberto) => setPainelParceiros((atual) => ({ ...atual, aberto }))}>
        <SheetContent>
          <SheetHeader className="pr-16">
            <SheetTitle>{painelParceiros.titulo}</SheetTitle>
          </SheetHeader>
          <div className="border-b border-border px-6 py-3">
            <SearchBar
              placeholder="Buscar parceiro"
              value={buscaPainelParceiros}
              onChange={(evento) => setBuscaPainelParceiros(evento.target.value)}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            <ul className="space-y-1">
              {painelParceiros.itens
                .filter((parceiro) => normalizeKey(parceiro.nome).includes(normalizeKey(buscaPainelParceiros)))
                .map((parceiro, index) => (
                  <li key={`painel-parceiro-${parceiro.id ?? index}`}>
                    <button
                      type="button"
                      disabled={!parceiro.id}
                      onClick={() => parceiro.id && navigate(`/parceiros/${parceiro.id}`)}
                      className="w-full truncate rounded-lg px-3 py-2 text-left text-sm text-foreground transition hover:bg-accent disabled:cursor-default disabled:hover:bg-transparent"
                    >
                      {parceiro.nome}
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        </SheetContent>
      </Sheet>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editingTask ? "Editar tarefa" : "Criar tarefa"}
        onSave={handleSalvarTarefa}
        onCancel={() => {
          setShowModal(false);
          resetForm();
        }}
        saveDisabled={savingTaskModal}
        saveText={savingTaskModal ? "Salvando..." : editingTask ? "Salvar alteracoes" : "Criar tarefa"}
        maxWidth="max-w-3xl"
        progress={tarefaModalProgress}
        progressLabel="Preenchimento da tarefa"
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <MultiSelectField
              label="Tipo de atividade"
              options={tiposOptions}
              selectedValues={form.tipoIds}
              onChange={(values) => {
                const nextTipoIds = values.length ? [String(values[0])] : [];
                const tipo = tiposTarefa.find((t) => String(t.id) === String(nextTipoIds[0] || ""));
                setForm((prev) => ({
                  ...prev,
                  tipoIds: nextTipoIds,
                  usuarioIds: prev.usuarioIds.length ? prev.usuarioIds : (isAdmin ? [] : (usuarioLogado?.id ? [String(usuarioLogado.id)] : [])),
                  vincularParceiros: allowsNoPartner(tipo) ? prev.parceiroIds.length > 0 : true,
                  parceiroIds: allowsMultiplePartners(tipo)
                    ? prev.parceiroIds
                    : prev.parceiroIds.slice(0, 1),
                }));
              }}
              single
              placeholder="Selecione o tipo"
              searchPlaceholder="Buscar tipo..."
            />
            <DatePickerField
              label="Data e hora da atividade"
              type="datetime-local"
              name="dataHora"
              value={form.dataHora}
              onChange={(e) => setForm((prev) => ({ ...prev, dataHora: e.target.value }))}
              min={taskScheduleBounds.min}
              max={taskScheduleBounds.max}
              required
              disabled={Boolean(editingTask && !podeEditarDataHora(editingTask))}
            />
          </div>

          <div className="rounded-md border border-border bg-muted/25 p-2 text-xs text-muted-foreground">
            A tarefa pode ser lancada ate {TASK_SCHEDULE_PAST_WINDOW_DAYS} dias no passado e no maximo para os proximos {TASK_SCHEDULE_WINDOW_DAYS} dias.
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              {permiteSemParceiroNoTipo ? (
                <label className="flex items-center gap-2 rounded-xl border border-border/70 bg-background/40 px-3 py-2 text-sm">
                  <Checkbox
                    checked={form.vincularParceiros}
                    onCheckedChange={(checked) =>
                      setForm((prev) => ({
                        ...prev,
                        vincularParceiros: checked,
                        parceiroIds: checked ? prev.parceiroIds : [],
                      }))
                    }
                  />
                  <span>Vincular parceiro(s) a esta tarefa</span>
                </label>
              ) : null}

              {vinculoSomenteUsuario ? (
                isAdmin ? (
                  <div className="space-y-2">
                    <MultiSelectField
                      label="Responsáveis"
                      required
                      options={responsavelOptions}
                      selectedValues={form.usuarioIds}
                      onChange={(values) => setForm((prev) => ({ ...prev, usuarioIds: values.map(String) }))}
                      single={Boolean(editingTask)}
                      placeholder="Selecione um ou mais responsáveis"
                      searchPlaceholder="Buscar responsável..."
                    />
                    {!editingTask && form.usuarioIds.length > 1 ? (
                      <p className="text-xs text-muted-foreground">
                        Serão criadas {form.usuarioIds.length} tarefas, uma para cada responsável — cada um conclui a
                        sua.
                      </p>
                    ) : null}
                    {editingTask ? (
                      <p className="text-xs text-muted-foreground">
                        Na edição, a tarefa tem um responsável só.
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <div className="rounded-3xl border border-border/70 bg-background/40 p-4 text-sm text-muted-foreground">
                    <p className="font-medium text-foreground">Responsavel travado pelo login</p>
                    <p className="mt-1">{usuarioLogado?.nome || "Usuario atual"}</p>
                  </div>
                )
              ) : (
                <MultiSelectField
                  label="Parceiros"
                  required={!permiteSemParceiroNoTipo}
                  options={parceiroTaskOptions}
                  selectedValues={form.parceiroIds}
                  onChange={(values) => {
                    const normalized = values.map(String);
                    setForm((prev) => ({
                      ...prev,
                      parceiroIds: permiteMultiplosParceirosNoTipo ? normalized : normalized.slice(-1),
                    }));
                  }}
                  single={!permiteMultiplosParceirosNoTipo}
                  placeholder={permiteSemParceiroNoTipo ? "Selecione parceiro(s) se houver" : "Selecione parceiro(s)"}
                  searchPlaceholder="Buscar parceiro..."
                />
              )}

              <p className="text-xs text-muted-foreground">
                {vinculoSomenteUsuario
                  ? "Esta tarefa sera vinculada apenas ao responsavel selecionado."
                  : permiteSemParceiroNoTipo
                    ? "Este tipo aceita atividade interna sem parceiro vinculado."
                    : "Este tipo exige ao menos um parceiro vinculado."}
              </p>
              {editingTask && !vinculoSomenteUsuario ? (
                <p className="text-xs text-muted-foreground">
                  Na edicao, so aparecem parceiros do responsavel atual da tarefa.
                </p>
              ) : null}
              {permiteMultiplosParceirosNoTipo && (form.parceiroIds?.length || 0) > 1 && !vinculoSomenteUsuario ? (
                <div className="space-y-2 rounded-xl border border-border bg-muted/20 p-3">
                  <p className="text-xs font-medium text-foreground">
                    {form.parceiroIds.length} parceiros selecionados. Como criar?
                  </p>
                  <label className="flex cursor-pointer items-start gap-2 text-xs text-foreground">
                    <input
                      type="radio"
                      name="modo-criacao-tarefa"
                      className="mt-0.5"
                      checked={umaTarefaPorParceiro}
                      onChange={() => setUmaTarefaPorParceiro(true)}
                    />
                    <span>
                      <strong>Uma tarefa para cada parceiro</strong> — {form.parceiroIds.length} tarefas. Cada uma é
                      concluída sozinha, então dá para ver quem já foi atendido e quem falta.
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-2 text-xs text-foreground">
                    <input
                      type="radio"
                      name="modo-criacao-tarefa"
                      className="mt-0.5"
                      checked={!umaTarefaPorParceiro}
                      onChange={() => setUmaTarefaPorParceiro(false)}
                    />
                    <span>
                      <strong>Uma tarefa só, com todos</strong> — agrupada por responsável comercial. Conclui de uma
                      vez para todo mundo.
                    </span>
                  </label>
                </div>
              ) : null}
            </div>

            {podeRegistrarConcluida ? (
              <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-border bg-muted/20 p-3 text-sm text-foreground">
                <Checkbox
                  checked={registrarComoConcluida}
                  onCheckedChange={(valor) => setRegistrarComoConcluida(valor)}
                />
                <span>
                  <strong>Já fiz — registrar como concluída</strong>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    Para o que já aconteceu. Cria e conclui na mesma ação, sem precisar abrir a tarefa depois.
                  </span>
                </span>
              </label>
            ) : null}

            <MultiSelectField
              label="Motivos"
              options={motivosOptions}
              selectedValues={form.motivoIds}
              onChange={(values) => setForm((prev) => ({ ...prev, motivoIds: values.map(String) }))}
              placeholder="Selecione motivo(s)"
              searchPlaceholder="Buscar motivo..."
            />
          </div>

          <FormField
            label="Observacoes"
            type="textarea"
            value={form.observacoes}
            onChange={(e) => setForm((prev) => ({ ...prev, observacoes: e.target.value }))}
            placeholder="Detalhes iniciais da tarefa"
          />

          <div className="rounded-md border border-border bg-muted/35 p-2 text-xs text-muted-foreground">
            <CalendarClock className="mr-1 inline h-3.5 w-3.5" />
            Status nao e informado na criacao: toda tarefa nova entra como Planejada.
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showExecModal}
        onClose={() => {
          setShowExecModal(false);
          setExecTask(null);
          setChecks([]);
          setExecTaskHistory([]);
          setGeoCheckDrawer((current) => ({ ...current, open: false }));
        }}
        onCancel={() => {
          setShowExecModal(false);
          setExecTask(null);
          setChecks([]);
          setExecTaskHistory([]);
          setGeoCheckDrawer((current) => ({ ...current, open: false }));
        }}
        onSave={concluirExecucao}
        title="Concluir tarefa"
        saveText={acaoLoading ? "Processando..." : !podeConcluir(execTask) ? "Tarefa concluida" : "Concluir tarefa"}
        saveDisabled={acaoLoading || !podeConcluir(execTask)}
        maxWidth="max-w-6xl"
        maxBodyHeight="max-h-[82vh]"
        progress={conclusaoProgress}
        progressLabel="Progresso da conclusao"
      >
        {!execTask ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Selecione uma tarefa.</div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/20 p-3 text-sm">
              <p><strong>Tipo:</strong> {tiposTarefa.find((t) => String(t.id) === String(execTask.idTipoDeTarefa))?.nomeTipo || "N/A"}</p>
              <p><strong>Motivos:</strong> {formatMotivosLabel(execTask)}</p>
              <p><strong>Data:</strong> {execTask.dataInicio ? new Date(execTask.dataInicio).toLocaleString("pt-BR") : "N/A"}</p>
              <p>
                <strong>Observacoes:</strong>{" "}
                <span className="whitespace-pre-wrap break-words">
                  {execTask.observacoes || execTask.descricaoTarefa || "N/A"}
                </span>
              </p>
            </div>

            {!podeConcluir(execTask) && (
              <div className="rounded-md border border-border bg-muted/35 p-2 text-xs text-muted-foreground">
                Essa tarefa ja foi concluida/cancelada ou nao pode ser concluida no seu perfil. Os dados estao em modo de visualizacao.
              </div>
            )}

            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              <span className="rounded-full border border-border bg-muted/20 px-2 py-1">
                {checkinExigido ? "Check-in habilitado" : "Sem check-in"}
              </span>
              <span className="rounded-full border border-border bg-muted/20 px-2 py-1">
                {checkoutExigido ? "Check-out habilitado" : "Sem check-out"}
              </span>
              <span className="rounded-full border border-border bg-muted/20 px-2 py-1">
                {permiteFallback ? (fallbackExigeAprovacao ? "Fallback com aprovacao" : "Fallback livre") : "Sem fallback"}
              </span>
              <span className="rounded-full border border-border bg-muted/20 px-2 py-1">
                {exigeEvidencia ? (evidenciaExigeAprovacao ? "Evidencia com aprovacao" : "Evidencia obrigatoria") : "Sem evidencia"}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Button
                variant="outline"
                className={
                  checkinOk
                    ? toneClassMap.successOutlineButtonActive
                    : toneClassMap.successOutlineButton
                }
                onClick={() => setGeoCheckDrawer({ open: true, action: "checkin" })}
                disabled={execLoading || acaoLoading || !checkinExigido || checkinOk || !podeConcluir(execTask)}
              >
                <LogIn className="h-4 w-4" />
                {checkinOk ? "Check-in realizado" : checkinExigido ? "Realizar check-in" : "Check-in nao exigido"}
              </Button>
              <Button
                variant="outline"
                className={
                  checkoutOk
                    ? toneClassMap.dangerOutlineButtonActive
                    : toneClassMap.dangerOutlineButton
                }
                onClick={() => setGeoCheckDrawer({ open: true, action: "checkout" })}
                disabled={execLoading || acaoLoading || !checkoutExigido || checkoutOk || !podeConcluir(execTask)}
              >
                <LogOut className="h-4 w-4" />
                {checkoutOk ? "Check-out realizado" : checkoutExigido ? "Realizar check-out" : "Check-out nao exigido"}
              </Button>
            </div>

            {(checkinPendente || checkoutPendente || evidenciaPendente) && (
              <div className={`rounded-md p-2 text-xs ${toneClassMap.warningPanel}`}>
                Existe check pendente de aprovacao. Enquanto houver pendencia, a conclusao da tarefa fica bloqueada.
              </div>
            )}

            {exigeEvidencia && (
              <section className="space-y-3 rounded-lg border border-border p-3">
                <h3 className="text-sm font-semibold">Evidencia de conclusao</h3>
                {checkEvidencia ? (
                  <div className="rounded-xl border border-border bg-muted/20 p-3.5 space-y-3.5">
                    <div>
                      <span className="text-[11px] font-semibold text-muted-foreground block uppercase tracking-wider">Observação Registrada</span>
                      <p className="text-sm text-foreground mt-1 whitespace-pre-wrap">{checkEvidencia.motivoFallback || "Nenhuma observação informada."}</p>
                    </div>
                    {checkEvidencia.fotoUrls && checkEvidencia.fotoUrls.length > 0 && (
                      <div>
                        <span className="text-[11px] font-semibold text-muted-foreground block uppercase tracking-wider mb-2">Imagens Enviadas</span>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                          {checkEvidencia.fotoUrls.map((url) => {
                            const absoluteUrl = `${httpClient.defaults.baseURL}${url}`;
                            return (
                              <a 
                                key={url} 
                                href={absoluteUrl} 
                                target="_blank" 
                                rel="noreferrer" 
                                className="overflow-hidden rounded-xl border border-border/70 group relative block aspect-video bg-muted/10 hover:border-primary/50 transition-colors shadow-sm"
                              >
                                <img 
                                  src={absoluteUrl} 
                                  alt="Evidência" 
                                  className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-250 ease-out" 
                                />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <FormField
                      label="Observacao da evidencia"
                      value={evidenciaForm.observacao}
                      onChange={(e) => setEvidenciaForm((prev) => ({ ...prev, observacao: e.target.value }))}
                      placeholder="Detalhes da evidencia enviada"
                    />
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-foreground">
                        Imagens
                        {evidenciaExigeImagem ? <span className="ml-1 text-destructive">*</span> : null}
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) =>
                          setEvidenciaForm((prev) => ({
                            ...prev,
                            files: Array.from(e.target.files || []),
                          }))
                        }
                        className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                      />
                      {evidenciaForm.files.length > 0 && (
                        <p className="text-xs text-muted-foreground">{evidenciaForm.files.length} arquivo(s) selecionado(s).</p>
                      )}
                    </div>

                    {evidenciaPreviews.length > 0 && (
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {evidenciaPreviews.map((preview) => (
                          <div key={`evidencia-${preview.url}`} className="overflow-hidden rounded-md border border-border/70">
                            <img src={preview.url} alt={preview.name} className="h-20 w-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                <Button
                  type="button"
                  variant="outline"
                  disabled={acaoLoading || evidenciaOk || evidenciaPendente || !podeConcluir(execTask)}
                  onClick={enviarEvidencia}
                >
                  {acaoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {evidenciaPendente 
                    ? "Evidencia pendente de aprovação" 
                    : evidenciaOk 
                      ? "Evidencia registrada" 
                      : "Enviar evidencia"}
                </Button>
                <div className="rounded-md border border-border bg-muted/35 p-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
                  A evidencia aparece porque o tipo exige evidencia. {evidenciaExigeAprovacao ? "Ela precisa de aprovacao do admin." : "Ela nao precisa de aprovacao."}
                </div>
              </section>
            )}

            {algumCampoFinanceiroHabilitado && (
              <section className="space-y-3 rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Campos da conclusao</h3>
                  {algumCampoLinhaHabilitado ? (
                    <Button type="button" size="sm" variant="outline" onClick={() => setConcluirForm((prev) => ({ ...prev, bancosConvenios: [...prev.bancosConvenios, createBancoLinha()] }))}>
                      <Plus className="h-3.5 w-3.5" />
                      Adicionar linha
                    </Button>
                  ) : null}
                </div>

                {exibirProdutoNegociado ? (
                  <FormField
                    label="Produto negociado"
                    required={produtoObrigatorio}
                    value={concluirForm.produto || ""}
                    onChange={(e) => setConcluirForm((prev) => ({ ...prev, produto: e.target.value }))}
                    placeholder="Descreva o produto negociado"
                  />
                ) : null}

                {algumCampoLinhaHabilitado ? concluirForm.bancosConvenios.map((linha, idx) => {
                  const totalCamposVisiveis = [
                    exibirBancoNegociado,
                    exibirConvenioNegociado,
                    exibirValorProduzido,
                    exibirConcorrente,
                  ].filter(Boolean).length;

                  return (
                    <div
                      key={`linha-banco-${idx}`}
                      className="grid grid-cols-1 gap-3 rounded-md border border-border/70 p-3 md:grid-cols-[repeat(4,minmax(0,1fr))_auto]"
                    >
                      {exibirBancoNegociado ? (
                        <MultiSelectField
                          label="Banco"
                          required={bancoObrigatorio}
                          options={bancosOptions}
                          selectedValues={linha.bancoId ? [String(linha.bancoId)] : []}
                          onChange={(values) => {
                            const nextBancoId = values?.[0] ? String(values[0]) : "";
                            setConcluirForm((prev) => ({
                              ...prev,
                              bancosConvenios: prev.bancosConvenios.map((item, itemIdx) =>
                                itemIdx === idx
                                  ? { ...item, bancoId: nextBancoId }
                                  : item
                              ),
                            }));
                          }}
                          single
                          placeholder="Selecione banco"
                          searchPlaceholder="Buscar banco..."
                        />
                      ) : (
                        <div />
                      )}

                      {exibirConvenioNegociado ? (
                        <MultiSelectField
                          label="Convenio"
                          required={convenioObrigatorio}
                          options={conveniosOptions}
                          selectedValues={linha.convenioId ? [String(linha.convenioId)] : []}
                          onChange={(values) =>
                            setConcluirForm((prev) => ({
                              ...prev,
                              bancosConvenios: prev.bancosConvenios.map((item, itemIdx) =>
                                itemIdx === idx ? { ...item, convenioId: values?.[0] ? String(values[0]) : "" } : item
                              ),
                            }))
                          }
                          single
                          placeholder="Selecione convênio"
                          searchPlaceholder="Buscar convênio..."
                        />
                      ) : (
                        <div />
                      )}

                      {exibirValorProduzido ? (
                        <FormField
                          label="Valor produzido"
                          required={valorObrigatorio}
                          type="text"
                          inputMode="decimal"
                          value={linha.valorProduzido || ""}
                          onChange={(e) =>
                            setConcluirForm((prev) => ({
                              ...prev,
                              bancosConvenios: prev.bancosConvenios.map((item, itemIdx) =>
                                itemIdx === idx ? { ...item, valorProduzido: e.target.value } : item
                              ),
                            }))
                          }
                          onBlur={() =>
                            setConcluirForm((prev) => ({
                              ...prev,
                              bancosConvenios: prev.bancosConvenios.map((item, itemIdx) =>
                                itemIdx === idx
                                  ? { ...item, valorProduzido: formatMoneyBRL(item.valorProduzido) || "" }
                                  : item
                              ),
                            }))
                          }
                          placeholder="Ex.: R$ 12.000,50"
                        />
                      ) : (
                        <div />
                      )}

                      {exibirConcorrente ? (
                        <div className="space-y-2">
                          <MultiSelectField
                            label="Concorrente"
                            required={concorrenteObrigatorio}
                            options={concorrentesOptions}
                            selectedValues={linha.concorrenteId ? [String(linha.concorrenteId)] : []}
                            onChange={(values) => {
                              const nextConcorrenteId = values?.[0] ? String(values[0]) : "";
                              setConcluirForm((prev) => ({
                                ...prev,
                                bancosConvenios: prev.bancosConvenios.map((item, itemIdx) =>
                                  itemIdx === idx
                                    ? {
                                      ...item,
                                      concorrenteId: nextConcorrenteId,
                                      concorrenteOutro: nextConcorrenteId === "OUTRO" ? item.concorrenteOutro : "",
                                    }
                                    : item
                                ),
                              }));
                            }}
                            single
                            placeholder="Selecione concorrente"
                            searchPlaceholder="Buscar concorrente..."
                          />

                          {String(linha.concorrenteId || "").toUpperCase() === "OUTRO" && (
                            <FormField
                              label="Qual concorrente?"
                              value={linha.concorrenteOutro || ""}
                              onChange={(e) =>
                                setConcluirForm((prev) => ({
                                  ...prev,
                                  bancosConvenios: prev.bancosConvenios.map((item, itemIdx) =>
                                    itemIdx === idx ? { ...item, concorrenteOutro: e.target.value } : item
                                  ),
                                }))
                              }
                              placeholder="Digite o nome do concorrente"
                            />
                          )}
                        </div>
                      ) : (
                        <div />
                      )}

                      <div className="flex items-end justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() =>
                            setConcluirForm((prev) => ({
                              ...prev,
                              bancosConvenios: prev.bancosConvenios.length > 1
                                ? prev.bancosConvenios.filter((_, i) => i !== idx)
                                : [createBancoLinha()],
                            }))
                          }
                        >
                          Remover
                        </Button>
                      </div>

                      {totalCamposVisiveis === 0 && (
                        <div className="col-span-full text-xs text-muted-foreground">
                          Nenhum campo financeiro habilitado para os motivos desta tarefa.
                        </div>
                      )}
                    </div>
                  );
                }) : (
                  <div className="rounded-md border border-dashed border-border/70 p-3 text-xs text-muted-foreground">
                    Nenhum campo em linha foi habilitado para os motivos desta tarefa.
                  </div>
                )}
              </section>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">Foi ajustado comissao do parceiro?</p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={concluirForm.comissaoParceiroAjustada === true ? "secondary" : "outline"}
                    onClick={() => setConcluirForm((prev) => ({ ...prev, comissaoParceiroAjustada: true }))}
                    disabled={acaoLoading}
                  >
                    <Check className="h-4 w-4" />
                    Sim
                  </Button>
                  <Button
                    type="button"
                    variant={concluirForm.comissaoParceiroAjustada === false ? "secondary" : "outline"}
                    onClick={() => setConcluirForm((prev) => ({ ...prev, comissaoParceiroAjustada: false }))}
                    disabled={acaoLoading}
                  >
                    <XCircle className="h-4 w-4" />
                    Nao
                  </Button>
                  <Button
                    type="button"
                    variant={concluirForm.comissaoParceiroAjustada == null ? "secondary" : "outline"}
                    onClick={() => setConcluirForm((prev) => ({ ...prev, comissaoParceiroAjustada: null }))}
                    disabled={acaoLoading}
                  >
                    Nao informado
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">Foi solicitado login do parceiro?</p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={concluirForm.loginParceiroSolicitado === true ? "secondary" : "outline"}
                    onClick={() => setConcluirForm((prev) => ({ ...prev, loginParceiroSolicitado: true }))}
                    disabled={acaoLoading}
                  >
                    <Check className="h-4 w-4" />
                    Sim
                  </Button>
                  <Button
                    type="button"
                    variant={concluirForm.loginParceiroSolicitado === false ? "secondary" : "outline"}
                    onClick={() => setConcluirForm((prev) => ({ ...prev, loginParceiroSolicitado: false }))}
                    disabled={acaoLoading}
                  >
                    <XCircle className="h-4 w-4" />
                    Nao
                  </Button>
                  <Button
                    type="button"
                    variant={concluirForm.loginParceiroSolicitado == null ? "secondary" : "outline"}
                    onClick={() => setConcluirForm((prev) => ({ ...prev, loginParceiroSolicitado: null }))}
                    disabled={acaoLoading}
                  >
                    Nao informado
                  </Button>
                </div>
              </div>
            </div>

            <FormField
              label="Detalhes da conclusao"
              type="textarea"
              value={concluirForm.relatorio}
              onChange={(e) => setConcluirForm((prev) => ({ ...prev, relatorio: e.target.value }))}
              placeholder="Descreva execucao, resultados e pendencias."
              inputClassName="min-h-[180px] sm:min-h-[220px]"
            />
            <div className="text-xs text-muted-foreground">Caracteres: {(concluirForm.relatorio || "").trim().length}</div>

            <div className="rounded-md border border-border bg-muted/35 p-2 text-xs text-muted-foreground">
              <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
              A conclusao valida regras do tipo de tarefa e campos obrigatorios por motivo.
            </div>

            <section className="space-y-3 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">Timeline da tarefa</h3>
                <span className="text-xs text-muted-foreground">
                  {Number(execTaskHistory.length || 0).toLocaleString("pt-BR")} evento(s)
                </span>
              </div>

              <div className="max-h-[26rem] space-y-3 overflow-y-auto pr-1">
                {execTaskHistoryLoading ? (
                  <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border/70 p-4 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Carregando timeline da tarefa...
                  </div>
                ) : execTaskHistory.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border/70 p-4 text-sm text-muted-foreground">
                    Nenhum evento registrado para esta tarefa.
                  </div>
                ) : (
                  execTaskHistory.map((evento) => {
                    const Icon = getTaskHistoryIcon(evento.categoria);
                    const campoLabel = evento?.payload?.campoLabel || evento?.payload?.campo || null;

                    return (
                      <article
                        key={`task-history-${evento.id}`}
                        className="rounded-2xl border border-border bg-card/80 p-3 shadow-sm"
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border ${getTaskHistoryToneClasses(
                              evento.categoria
                            )}`}
                          >
                            <Icon className="h-4 w-4" />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="min-w-0 flex-1 text-sm font-semibold leading-5 text-foreground">
                                {evento.resumo || evento.acao}
                              </p>
                              {campoLabel ? (
                                <span className="rounded-full border border-border bg-muted/25 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                                  {campoLabel}
                                </span>
                              ) : null}
                            </div>

                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              <Avatar className="h-7 w-7 rounded-xl">
                                <AvatarImage src={evento.atorFotoPerfilUrl} alt={evento.atorNome || "Webhook"} />
                                <AvatarFallback>{getAvatarInitials(evento.atorNome || "Webhook")}</AvatarFallback>
                              </Avatar>
                              <span className="font-medium">{evento.atorNome || "Webhook"}</span>
                              <span>&bull;</span>
                              <span>{formatDateTimeLabel(evento.createdAt)}</span>
                              <span>&bull;</span>
                              <span>{String(evento.acao || "").replaceAll("_", " ")}</span>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        )}
      </Modal>
      <TaskGeoCheckDrawer
        open={showExecModal && geoCheckDrawer.open}
        onOpenChange={(nextOpen) => setGeoCheckDrawer((current) => ({ ...current, open: nextOpen }))}
        action={geoCheckDrawer.action}
        task={execTask}
        partner={execTaskGeoPartner}
        canSubmit={podeConcluir(execTask)}
        busy={acaoLoading}
        fallbackEnabled={permiteFallback}
        fallbackRequiresImage={fallbackExigeImagem}
        fallbackRequiresApproval={fallbackExigeAprovacao}
        fallbackState={fallbackLiberado}
        fallbackForm={fallbackForm}
        fallbackPreviews={fallbackPreviews}
        onFallbackMotivoChange={(motivo) => setFallbackForm((prev) => ({ ...prev, motivo }))}
        onFallbackFilesChange={(files) => setFallbackForm((prev) => ({ ...prev, files }))}
        onConfirm={handleGeoCheckConfirm}
        onSubmitFallback={handleSubmitFallback}
      />
    </div>
  );
}
